/**
 * Workflow DSL Parser
 *
 * Parses a compact, line-oriented DSL script into an ordered list of typed
 * command objects.  The executor (workflow-dsl-executor.ts) walks the list
 * and calls existing action functions from designer-workflow-step-actions.ts.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * Grammar (line-oriented, one command per line, # comments, blank lines OK):
 *
 *   PARAM INTERNAL <name> <Type> [= <default>]
 *   PARAM BO <name> <Type> [INPUT] [OUTPUT] [= <default>]
 *   LANE <@alias> "<name>" [role:<roleId> roleName:"<name>" [assignmentType:<N>]]
 *   TASK <@alias> "<name>" [after:<ref>] [before:<ref>] [lane:<ref>]
 *   SYSTEM <@alias> "<name>" [after:<ref>] [before:<ref>] [lane:<ref>]
 *   XGATEWAY <@alias> ["<name>"] [after:<ref>] [before:<ref>]
 *   PGATEWAY <@alias> ["<name>"] [after:<ref>] [before:<ref>]
 *   IGATEWAY <@alias> ["<name>"] [after:<ref>] [before:<ref>]
 *   PJOIN <@alias> ["<name>"] [after:<ref>] [before:<ref>]
 *   IJOIN <@alias> ["<name>"] [after:<ref>] [before:<ref>]
 *   SUBPROCESS <@alias> "<name>" [after:<ref>]
 *   LINKED <@alias> ["<name>"] [after:<ref>]
 *   TIMER <@alias> ["<label>"] after:<ref> [before:<ref>] [due:<N><Unit>]
 *   BOUNDARY <@alias> on:<ref> type:<Timer|Message|Error>
 *   PATH <sourceRef> -> <targetRef> ["<name>"] [DEFAULT] [WHEN <param> <op> <value>]
 *   CONNECT <sourceRef> -> <targetRef>
 *   CONDITION <pathRef> WHEN <param> <op> <value> [OR <param> <op> <value>]...
 *   DEFAULT <gatewayRef> <pathRef> ["<label>"]
 *   RENAME <ref> "<newName>"
 *   DELETE <ref>
 *   MI <ref> <PMI|SMI|None> [count:<N>] [collection:<Manual|Assignment>] [expansion:<ByRole|ByUser>] [ordering:<Ascending|Descending>]
 *   MI_CANCEL <ref> WHEN <param> <op> <value>
 *   MI_AGG <ref> childKey:<key> strategy:<Majority|SumInt|SumDouble|AverageDouble|Min|Max>
 *   CONFIGURE_BOUNDARY <ref> [interrupting] [noninterrupting] [duration:<N><Unit>]
 *   TIMER_DUE <ref> <N><Unit>
 *   TASK_DUE <ref> <N><Unit>
 *   NOTIFY <ref> <on|off>
 *   INTERACTION <ref> "<label>"
 *   PRE_ACTION <ref> "<actionName>"
 *   STEP_ACTION <ref> "<actionName>"
 *   ASSIGN_DYNAMIC <taskRef> "<actionName>" returns:<User|Role>
 *   ASSIGN_INPUT <taskRef> <actionParamName> [=] <workflowParamName | "literal">
 *   MAP_OUTPUT <ref> "<parameterNameOrId>" [FROM <actionName>[.<resultField>]]
 *   PHASE "<phaseName>"
 *   PHASE_ASSIGN <ref> "<phaseName>"
 *   UPDATE_PARAM <paramId> [name:"<n>"] [type:<t>] [default:"<v>"] [input:<bool>] [output:<bool>]
 *   DELETE_PARAM <paramId>
 *   LANE_DELETE <ref>
 *   LANE_RENAME <ref> "<newName>"
 *   LANE_ASSIGN <ref> role:<roleId> roleName:"<name>" [assignmentType:<N>]
 *   LINKED_CONFIG <ref> target:<processId> [map:"<srcParam>"->"<tgtParam>"]...
 *   LINKED_RENAME <ref> "<newName>"
 *   LINKED_DELETE <ref>
 *   SUBPROCESS_RENAME <ref> "<newName>"
 *   SUBPROCESS_DELETE <ref>
 *   ASSIGN_SCREEN <processId> <stepRef> [<stepRef>...]
 *
 *   Task-screen composition (KOA-18431) — `#alias` is a component or an attached action,
 *   a separate namespace from the `@alias` of a BPMN node:
 *   PLACE <stepRef> <ComponentName> AS <#alias> IN <screen|header|footer|#alias|kuikaId>
 *   CHILD <#host> <ComponentName> AS <#alias> [first]
 *   FIND <stepRef|#host> <ComponentName> AS <#alias> [n]
 *   SET <#alias> <propertyName> [=] <value>
 *   BIND <#alias> <propertyName> <source>
 *   DATA <#alias> <propertyName> "<ActionName>"|fixed
 *   DATAFIELD <#alias> <propertyName> <key> <value>
 *   ACTION <stepRef|#alias> <eventName> "<ActionName>" [AS <#actionAlias>]
 *   ARG <#actionAlias> "<parameterName>" <source>
 *   ORDER <#alias> BEFORE <#sibling>
 *   DROP <#alias>
 *   Sources: "<literal>" | process:<name> | screenInput:<name> | component:<#alias>.<property>
 *            | action:<#alias> | field:<#alias>.<Field> | row:<Field> | default:<Name>
 *
 *   SELECT <ref>
 *   LIST [<workflowId>]
 *   LINT
 *   LAYOUT
 *
 * Refs: @alias or literal BPMN IDs (e.g. StartEvent_1, Activity_0abc).
 * Operators: == != > < isEmpty isNotEmpty (mapped to isEqualTo etc.)
 * Duration: <N><Unit> e.g. 5Hours, 30Minutes, 2Days
 * ───────────────────────────────────────────────────────────────────────────
 */

import type { SpecialDIIds } from "@kuika/kuika-cl-model";
import { normalizeWorkflowParamType } from "../../workflow-param-types";

// ── Command types ──────────────────────────────────────────────────────────

interface BaseCommand {
  line: number;
}

export interface ParamInternalCmd extends BaseCommand {
  kind: "PARAM_INTERNAL";
  name: string;
  dataType: string;
  isInput: boolean;
  isOutput: boolean;
  defaultValue?: string;
}

export interface ParamBoCmd extends BaseCommand {
  kind: "PARAM_BO";
  name: string;
  dataType: string;
  isInput: boolean;
  isOutput: boolean;
  defaultValue?: string;
  /**
   * True for `PARAM BO <name> OBJECT <TypeName>` — the parameter references a Kuika Data
   * Type and is emitted as a typed POCO instead of a scalar (KOA-15840).
   */
  isObject?: boolean;
  /** The referenced catalog type name, verbatim as written; resolved against the catalog at execution. */
  objectTypeName?: string;
  /** True for the `LIST` modifier — a list of that type rather than a single one. */
  isList?: boolean;
}

export interface LaneCmd extends BaseCommand {
  kind: "LANE";
  alias: string;
  name: string;
  roleId?: string;
  roleName?: string;
  assignmentType?: number;
}

export interface TaskCmd extends BaseCommand {
  kind: "TASK";
  alias: string;
  name: string;
  afterNodeId?: string;
  beforeNodeId?: string;
  laneId?: string;
}

export interface SystemCmd extends BaseCommand {
  kind: "SYSTEM";
  alias: string;
  name: string;
  afterNodeId?: string;
  beforeNodeId?: string;
  laneId?: string;
}

export interface GatewayCmd extends BaseCommand {
  kind: "GATEWAY";
  alias: string;
  gatewayType: "Exclusive" | "Inclusive" | "Parallel";
  role: "diverge" | "converge";
  name?: string;
  afterNodeId?: string;
  beforeNodeId?: string;
}

export interface SubprocessCmd extends BaseCommand {
  kind: "SUBPROCESS";
  alias: string;
  name: string;
  afterNodeId?: string;
}

export interface LinkedCmd extends BaseCommand {
  kind: "LINKED";
  alias: string;
  name?: string;
  afterNodeId?: string;
}

export interface TimerCmd extends BaseCommand {
  kind: "TIMER";
  alias: string;
  afterNodeId: string;
  beforeNodeId?: string;
  /** Optional label (e.g. "2 Saat Bekle"). Used as the timer's display name
   * and mined for a duration fallback when no explicit due: is given. */
  name?: string;
  /** Optional inline wait duration (e.g. due:2Hours). Without it the timer
   * defaults to 1 day in the workflow sync layer — so a request like
   * "2 saat bekleyen Timer" MUST carry due:2Hours (or encode it in the label). */
  dueValue?: number;
  dueUnit?: string;
}

export interface BoundaryCmd extends BaseCommand {
  kind: "BOUNDARY";
  alias: string;
  nodeId: string;
  eventType: "TimerEventDefinition" | "MessageEventDefinition" | "ErrorEventDefinition";
}

export interface PathCmd extends BaseCommand {
  kind: "PATH";
  sourceNodeId: string;
  targetNodeId: string;
  pathName?: string;
  isDefault: boolean;
  conditions?: ConditionEntry[];
  isOrGroup?: boolean;
}

export interface ConnectCmd extends BaseCommand {
  kind: "CONNECT";
  sourceNodeId: string;
  targetNodeId: string;
}

export interface ConditionCmd extends BaseCommand {
  kind: "CONDITION";
  pathId: string;
  conditions: ConditionEntry[];
  isOrGroup: boolean;
}

export interface DefaultCmd extends BaseCommand {
  kind: "DEFAULT";
  gatewayId: string;
  pathId: string;
  /** Optional label to apply to the default branch in the same step
   *  ("default dalı 'Diğer Durumlar' olsun" → mark default AND name it). */
  pathName?: string;
}

export interface RenameCmd extends BaseCommand {
  kind: "RENAME";
  elementId: string;
  newName: string;
}

export interface DescribeCmd extends BaseCommand {
  kind: "DESCRIBE";
  stepId: string;
  description: string;
}

export interface DeleteCmd extends BaseCommand {
  kind: "DELETE";
  elementId: string;
}

export interface MiCmd extends BaseCommand {
  kind: "MI";
  stepId: string;
  mode: "PMI" | "SMI" | "None";
  instanceCount?: number;
  collectionSource?: "Manual" | "Assignment";
  expansionMode?: "ByRole" | "ByUser";
  ordering?: "Ascending" | "Descending";
}

export interface MiCancelCmd extends BaseCommand {
  kind: "MI_CANCEL";
  stepId: string;
  conditions: ConditionEntry[];
  isOrGroup: boolean;
}

export interface MiAggCmd extends BaseCommand {
  kind: "MI_AGG";
  stepId: string;
  childOutputKey: string;
  strategy: string;
}

export interface ConfigureBoundaryCmd extends BaseCommand {
  kind: "CONFIGURE_BOUNDARY";
  eventId: string;
  isInterrupting: boolean;
  dueValue?: number;
  dueUnit?: string;
}

export interface TimerDueCmd extends BaseCommand {
  kind: "TIMER_DUE";
  eventId: string;
  dueValue: number;
  dueUnit: string;
}

export interface TaskDueCmd extends BaseCommand {
  kind: "TASK_DUE";
  taskId: string;
  dueValue: number;
  dueUnit: string;
}

export interface NotifyCmd extends BaseCommand {
  kind: "NOTIFY";
  taskId: string;
  sendNotification: boolean;
}

/** One `SET <name>=<value>` assignment on an INTERACTION — a workflow OUTPUT
 *  parameter name paired with the fixed value this button writes into it. The
 *  value is kept as the raw string here; the designer action coerces it to the
 *  parameter's dataType (Boolean/numeric/…) when it resolves the name to a
 *  real workflow parameter. */
export interface InteractionSetAssignment {
  name: string;
  value: string;
}

export interface InteractionCmd extends BaseCommand {
  kind: "INTERACTION";
  taskId: string;
  label: string;
  /** `SET name=value` decision payload — which task OUTPUT params this button
   *  sets and to what fixed value (e.g. Onayla → isApproved=true). Empty when
   *  the interaction is a bare labelled button with no decision payload. */
  parameters: InteractionSetAssignment[];
}

export interface PreActionCmd extends BaseCommand {
  kind: "PRE_ACTION";
  taskId: string;
  actionName: string;
}

export interface StepActionCmd extends BaseCommand {
  kind: "STEP_ACTION";
  stepId: string;
  actionName: string;
}

export interface MapOutputCmd extends BaseCommand {
  kind: "MAP_OUTPUT";
  nodeId: string;
  parameterId: string;
  /**
   * `FROM <actionName>` — the step action whose RESULT feeds this output. Only
   * meaningful on a system step; a user task's outputs are written by its
   * INTERACTION buttons / task form, not by an action.
   */
  sourceActionName?: string;
  /** `FROM <actionName>.<field>` — the field of the result to take. Omit for a scalar result. */
  sourceFieldName?: string;
}

/**
 * KOA-17362 Adım 3 — declare a Happy Path phase (the engine's human-readable
 * business status for a running instance). Idempotent by name; `phaseOrder`
 * derives from declaration order. See `workflowDeclareHappyPathPhaseAction`.
 */
export interface PhaseCmd extends BaseCommand {
  kind: "PHASE";
  name: string;
}

/**
 * KOA-17362 Adım 3 — attach a step to a declared phase, so the engine sets the
 * instance's `Status` to that phase name when the step becomes current. Phases
 * are addressed by NAME (not by an `@alias`) — a phase is named metadata like a
 * parameter or an action, and name addressing lets a later script assign without
 * re-declaring. See `workflowAssignStepHappyPathPhaseAction`.
 */
export interface PhaseAssignCmd extends BaseCommand {
  kind: "PHASE_ASSIGN";
  stepId: string;
  phaseName: string;
}

/**
 * KOA-18593 — a USER task assigned by an action that returns who gets it (the platform's dynamic
 * assignment). `returns` says what each returned row's `Id` names: a person (their user id) or a
 * role (its name). See `workflowSetDynamicAssignmentAction`.
 */
export interface AssignDynamicCmd extends BaseCommand {
  kind: "ASSIGN_DYNAMIC";
  taskId: string;
  actionName: string;
  returns: "User" | "Role";
}

/** KOA-18593 — one input of a task's dynamic assignment action, bound like `MAP_INPUT`. */
export interface AssignInputCmd extends BaseCommand {
  kind: "ASSIGN_INPUT";
  taskId: string;
  parameterName: string;
  source: string;
  isLiteral: boolean;
}

export interface MapInputCmd extends BaseCommand {
  kind: "MAP_INPUT";
  stepId: string;
  parameterName: string;
  /** Workflow-parameter name to bind into the action input, OR a literal fixed value. */
  source: string;
  /** True when `source` was a quoted literal ("…"); false when it's a bare workflow-parameter reference. */
  isLiteral: boolean;
}

export interface UpdateParamCmd extends BaseCommand {
  kind: "UPDATE_PARAM";
  parameterId: string;
  updates: {
    name?: string;
    dataType?: string;
    defaultValue?: string;
    isInput?: boolean;
    isOutput?: boolean;
  };
}

export interface DeleteParamCmd extends BaseCommand {
  kind: "DELETE_PARAM";
  parameterId: string;
}

export interface LaneDeleteCmd extends BaseCommand {
  kind: "LANE_DELETE";
  laneId: string;
}

export interface LaneRenameCmd extends BaseCommand {
  kind: "LANE_RENAME";
  laneId: string;
  newName: string;
}

export interface LaneAssignCmd extends BaseCommand {
  kind: "LANE_ASSIGN";
  laneId: string;
  /** Absent when the line named its role and left the id to the platform. */
  roleId?: string;
  roleName: string;
  assignmentType: number;
}

export interface LinkedConfigCmd extends BaseCommand {
  kind: "LINKED_CONFIG";
  linkedProcessId: string;
  targetProcessId: string;
  parameters: { sourceParameterName: string; targetParameterName: string }[];
}

export interface LinkedRenameCmd extends BaseCommand {
  kind: "LINKED_RENAME";
  linkedProcessId: string;
  newName: string;
}

export interface LinkedDeleteCmd extends BaseCommand {
  kind: "LINKED_DELETE";
  linkedProcessId: string;
}

export interface SubprocessRenameCmd extends BaseCommand {
  kind: "SUBPROCESS_RENAME";
  subprocessId: string;
  newName: string;
}

export interface SubprocessDeleteCmd extends BaseCommand {
  kind: "SUBPROCESS_DELETE";
  subprocessId: string;
}

export interface AssignScreenCmd extends BaseCommand {
  kind: "ASSIGN_SCREEN";
  processId: string;
  stepIds: string[];
}

/**
 * KOA-17362 §7.6 Eksen 1 — per-field configuration of a user task's MANAGED task
 * screen. Declarative: the executor collects these and hands them to ASSIGN_SCREEN,
 * which merges them into the form metadata it generates. Every knob here is one the
 * human form wizard already exposes and the backend renderer already honors.
 */
export interface ScreenFieldCmd extends BaseCommand {
  kind: "SCREEN_FIELD";
  stepId: string;
  /** Workflow business-object parameter name (as declared by `PARAM BO`). */
  param: string;
  /** true = not rendered, false = explicitly rendered. Undefined = leave as-is. */
  hidden?: boolean;
  /** true = read-only view, false = editable input. Undefined = leave as-is. */
  isReadonly?: boolean;
  /** true = required validation, false = optional. Undefined = leave as-is. */
  isRequired?: boolean;
  /** Visible label for the field. */
  label?: string;
  /** 1-based position within the form. */
  order?: number;
  /** Input widget name, as written by the author (resolved to a canonical constant at apply time). */
  element?: string;
  /** Decimal places for a numeric field. */
  scale?: number;
  /** Title of the section this field belongs to (created if it does not exist). */
  section?: string;
}

/**
 * KOA-17362 §7.6 Eksen 1 (slice 3) — a TITLED section of a user task's managed form.
 * Declarative like SCREEN_FIELD: ASSIGN_SCREEN creates the section when it generates
 * the screen, and `SCREEN_FIELD … section:"<title>"` routes fields into it. Declaring a
 * section is also what fixes its ORDER on the screen and its responsive layout.
 */
export interface ScreenSectionCmd extends BaseCommand {
  kind: "SCREEN_SECTION";
  stepId: string;
  title: string;
  /** Desktop columns (1 | 2 | 4). */
  columns?: number;
  /** Tablet columns (1 | 2 | 4). */
  tablet?: number;
  /** Mobile columns (1 | 2 | 4). */
  mobile?: number;
}

/** KOA-17362 §7.6 — responsive layout of the form's DEFAULT (untitled) section. */
export interface ScreenFormCmd extends BaseCommand {
  kind: "SCREEN_FORM";
  stepId: string;
  columns?: number;
  tablet?: number;
  mobile?: number;
}

/**
 * KOA-17362 §7.6 slice 4 — where the ResumeProcess / decision buttons sit on managed
 * task screens. PROJECT-WIDE by design (`project.workflowScreensSettings`), so it takes
 * no step ref: the platform has no per-step setting and pretending otherwise would make
 * the model author one per task.
 */
export interface ScreenButtonsCmd extends BaseCommand {
  kind: "SCREEN_BUTTONS";
  /** "under" | "top" | "bottom" as written by the author. */
  location: string;
}

/**
 * KOA-18431 — COMPOSING a user task's managed screen from the script that builds the process.
 *
 * Everything below drives a capability that already exists and is already tested: the detached
 * screen session, the screen-agnostic tree operations, the composition ledger and payload, and the
 * server's applier. That capability was reachable from a hand-driven UI in the process editor,
 * which was removed on purpose — the party that knows what a task screen must DO is the one
 * holding the business-analyst spec, and that party authors the process here. These commands are
 * the consumer the removal left missing.
 *
 * ## Two alias namespaces, two sigils
 *
 * `@name` is a BPMN node. `#name` is something on a SCREEN — a component this script placed, or an
 * action instance it attached. They are resolved from different tables and can never collide, so
 * `TASK @approve` and `PLACE … AS #approve` are two unrelated names and neither shadows the other.
 *
 * ## Only PLACE names a step
 *
 * A component belongs to exactly one screen, so every later line reads the screen off the alias it
 * addresses. Repeating the step on every line would be noise the author has to keep consistent,
 * and an inconsistency there is a write on the wrong screen.
 */

/** Where a placement goes: a named screen root, a component this script placed, or a literal id. */
export type CompositionAnchor =
  /** One of the screen's own roots — `screen`, `header`, `footer`, the layout regions. */
  | { kind: "root"; kuikaId: number }
  /** A component placed earlier in this script. */
  | { kind: "alias"; alias: string }
  /** A component already on the screen, addressed by the id the platform gave it. */
  | { kind: "kuikaId"; kuikaId: number };

/**
 * Where a bound value comes from — the same sources the platform's own binding picker offers,
 * spelled as one token so a line stays one line.
 *
 * A discriminated union rather than a bag of optional fields: each source needs exactly its own
 * operands, and an absent operand must be a parse error rather than an undefined that surfaces as
 * an empty binding three layers down.
 */
export type CompositionSource =
  /** `"text"` — a literal. */
  | { kind: "fixed"; value: string }
  /** `process:<name>` — a value the running process carries (only on a managed task screen). */
  | { kind: "processParameter"; name: string }
  /** `screenInput:<name>` — an input this screen declares. */
  | { kind: "screenInput"; name: string }
  /** `component:#alias.<property>` — another component's value. */
  | { kind: "componentValue"; alias: string; propertyName: string }
  /** `action:#actionAlias` — the whole result of an action attached by this script. */
  | { kind: "actionResult"; actionAlias: string }
  /** `field:#actionAlias.<Field>` — one field of that result. */
  | { kind: "actionField"; actionAlias: string; fieldName: string }
  /** `row:<Field>` — a field of the row, inside a data repeater. */
  | { kind: "currentRow"; fieldName: string }
  /** `default:<Name>` — a platform default (`CurrentUserid`, `DateNow`, …). */
  | { kind: "default"; name: string };

/** `PLACE <stepRef> <Component> AS #alias IN <anchor>` — put a catalog component on the screen. */
export interface PlaceCmd extends BaseCommand {
  kind: "PLACE";
  /** The user task whose managed screen this is. The only composition line that names one. */
  stepId: string;
  componentName: string;
  alias: string;
  anchor: CompositionAnchor;
}

/** `CHILD #host <Component> AS #alias [first]` — add one of a host's DECLARED child items. */
export interface ChildCmd extends BaseCommand {
  kind: "CHILD";
  hostAlias: string;
  componentName: string;
  alias: string;
  /** A band that belongs at the top (a table header). Default is last. */
  placeFirst: boolean;
}

/**
 * `FIND #host <Component> AS #alias [n]` — name a child the host ALREADY has.
 *
 * `PLACE` lands a SUBTREE, not a component: the catalog's `childrenInfo` carries a `count`, and
 * `ScreenDesignHelper.addChildForItemContainer` creates that many of each child with the host, then
 * recurses. A Table therefore arrives with its `TableHeader`, its `TableRow`, and the row's first
 * `TableDefaultColumn` and `TableTemplateColumn` — every one of them already built, and every one
 * of them nameless as far as the script is concerned, because `PLACE` hands back one alias for the
 * root and nothing for the tree underneath it.
 *
 * Without this verb the whole of that subtree is unreachable. A script could not title the column
 * the platform made, could not add a second one (columns hang off the ROW, and the row has no
 * alias), could not touch the Label inside a cell. `CHILD` is no answer: it ADDS, so asking it for
 * the row would give a table two rows.
 *
 * So `FIND` says the thing `CHILD` cannot: *this is already there, let me call it something*. It
 * creates nothing and stages nothing — the component it names was never the script's to insert.
 *
 * The host is a `#alias` this script named, or — like `ACTION` — a STEP REF, meaning that step's
 * screen BODY. The step form is the only way a script can address something that was already on
 * the screen when it started: re-decorating a task screen built by an earlier script is ordinary
 * work, and without it the second script can only `PLACE` a second copy of what is already there.
 *
 * `n` is 1-based and defaults to 1; it picks among same-named siblings (the second column of a
 * row), counting in the order the screen reads.
 *
 * The search is the host's whole SUBTREE, not its direct children. The author cannot see the
 * screen and the nesting is not theirs: a Table's columns hang off its row, and a component an
 * earlier script placed sits wherever that script put it. Making them state the path would be the
 * same trap as making them know that columns are the row's.
 */
export interface FindChildCmd extends BaseCommand {
  kind: "FIND";
  /** A component this script named, or the STEP whose screen body is searched. */
  host: CompositionHost;
  componentName: string;
  alias: string;
  /** Which same-named child to take, 1-based. */
  ordinal: number;
}

/** `SET #alias <property> = <value>` — a literal property value. */
export interface SetPropertyCmd extends BaseCommand {
  kind: "SET";
  alias: string;
  propertyName: string;
  value: string | number | boolean;
}

/** `BIND #alias <property> <source>` — a property fed from somewhere. */
export interface BindPropertyCmd extends BaseCommand {
  kind: "BIND";
  alias: string;
  propertyName: string;
  source: CompositionSource;
}

/**
 * `DATA #alias <property> "<ActionName>"` — point a DATA-EDITOR property at its source.
 *
 * A data-repeater's `dataSource`, a dropdown's `options` and a chart's series are not ordinary
 * properties: the platform edits them in a data editor of their own, which binds an ACTION and then
 * names which of its columns to read. `BIND` cannot express that — it writes one symbol — so these
 * two verbs are the data editor, and they exist because a task screen that needs a dropdown or a
 * bound table needs both halves.
 *
 * `fixed` in place of an action name is the platform's own fixed key/text option list.
 */
export interface SetDataActionCmd extends BaseCommand {
  kind: "DATA";
  alias: string;
  propertyName: string;
  /** The action's name as written, or `fixed` for a literal option list. */
  actionName: string;
}

/** `DATAFIELD #alias <property> <key> <value>` — one knob of that data editor. */
export interface SetDataFieldCmd extends BaseCommand {
  kind: "DATAFIELD";
  alias: string;
  propertyName: string;
  /** The editor key (`fieldToDisplay`, `fieldToUseAsKey`, `CalendarOptions.datastartfield`, …). */
  key: string;
  value: string;
}

/**
 * `OPTION #alias <property> "<value>" "<label>"` — ONE entry of a fixed option list.
 *
 * ## Why this verb has to exist
 *
 * `DATA #x options fixed` says the list is a literal one, and the platform's writer then creates it
 * EMPTY — that is what "fixed" means before anybody says what is in it. Nothing in this grammar
 * could say what is in it, so every `fixed` dropdown this surface produced shipped with no choices
 * at all (live `c74f551e`). The capability was never missing from the platform: the Binder fills
 * exactly this list on ordinary screens, entry by entry. It was missing from the DSL.
 *
 * One entry per line, rather than a list packed into one token: the value or the label can contain
 * anything a person writes, including the separator a packed form would need, and a quoted pair is
 * the shape the rest of this grammar already uses. Lines accumulate in the order they are written,
 * which is the order the dropdown shows.
 *
 * The first `OPTION` for a property also MAKES the list fixed, so `DATA … fixed` before it is
 * optional — an author who writes the entries has already said what they mean.
 */
export interface SetOptionCmd extends BaseCommand {
  kind: "OPTION";
  alias: string;
  propertyName: string;
  /** What the control submits — the value stored in the column. */
  value: string;
  /** What the person reads. Defaults to the value when the line gives only one. */
  label: string;
}

/** `ACTION #alias <event> "<ActionName>" [AS #actionAlias]` — attach an action to an event. */
/** WHERE an `ACTION` line hangs its action. `#alias` never collides with a step ref. */
export type CompositionHost =
  | { readonly kind: "alias"; readonly alias: string }
  | { readonly kind: "step"; readonly stepRef: string };

export interface AttachActionCmd extends BaseCommand {
  kind: "ACTION";
  /**
   * WHAT the action hangs off: a component this script placed, or a STEP — meaning that step's
   * screen BODY.
   *
   * The step arm exists because `pageInit` is an event of the body (`ROOT_SECTIONS["-1"]`), not of
   * any component on it. With only `#alias` accepted there was no way to say so, and live
   * `02cda57d` did the only thing left: it hung a screen-load read on a Table. The Table does not
   * declare `pageInit`, so the action sat in the tree, absent from the property panel and never
   * fired. Mirrors `PLACE`, whose first token is a step ref for the same reason.
   */
  host: CompositionHost;
  eventName: string;
  actionName: string;
  /** Names the attached INSTANCE, so `ARG` and `action:` / `field:` sources can reach it. */
  actionAlias?: string;
}

/** `ARG #actionAlias "<parameter>" <source>` — feed one input of an attached action. */
export interface ActionArgCmd extends BaseCommand {
  kind: "ARG";
  actionAlias: string;
  parameterName: string;
  source: CompositionSource;
}

/** `ORDER #alias BEFORE #sibling` — move a component in front of one of its own siblings. */
export interface ReorderCmd extends BaseCommand {
  kind: "ORDER";
  alias: string;
  beforeAlias: string;
}

/** `DROP #alias` — take a component off the screen. */
export interface DropCmd extends BaseCommand {
  kind: "DROP";
  target: CompositionAnchor;
}

export interface SelectCmd extends BaseCommand {
  kind: "SELECT";
  stepId: string;
}

export interface ListCmd extends BaseCommand {
  kind: "LIST";
  workflowId?: string;
}

export interface LintCmd extends BaseCommand {
  kind: "LINT";
}

export interface LayoutCmd extends BaseCommand {
  kind: "LAYOUT";
}

export interface UpdatePathNameCmd extends BaseCommand {
  kind: "UPDATE_PATH_NAME";
  pathId: string;
  newName: string;
}

export type DslCommand =
  | ParamInternalCmd
  | ParamBoCmd
  | LaneCmd
  | TaskCmd
  | SystemCmd
  | GatewayCmd
  | SubprocessCmd
  | LinkedCmd
  | TimerCmd
  | BoundaryCmd
  | PathCmd
  | ConnectCmd
  | ConditionCmd
  | DefaultCmd
  | RenameCmd
  | DescribeCmd
  | DeleteCmd
  | MiCmd
  | MiCancelCmd
  | MiAggCmd
  | ConfigureBoundaryCmd
  | TimerDueCmd
  | TaskDueCmd
  | NotifyCmd
  | InteractionCmd
  | PreActionCmd
  | StepActionCmd
  | MapOutputCmd
  | PhaseCmd
  | PhaseAssignCmd
  | MapInputCmd
  | AssignDynamicCmd
  | AssignInputCmd
  | UpdateParamCmd
  | DeleteParamCmd
  | LaneDeleteCmd
  | LaneRenameCmd
  | LaneAssignCmd
  | LinkedConfigCmd
  | LinkedRenameCmd
  | LinkedDeleteCmd
  | SubprocessRenameCmd
  | SubprocessDeleteCmd
  | AssignScreenCmd
  | ScreenFieldCmd
  | ScreenSectionCmd
  | ScreenFormCmd
  | ScreenButtonsCmd
  | PlaceCmd
  | ChildCmd
  | FindChildCmd
  | SetPropertyCmd
  | BindPropertyCmd
  | SetDataActionCmd
  | SetDataFieldCmd
  | SetOptionCmd
  | AttachActionCmd
  | ActionArgCmd
  | ReorderCmd
  | DropCmd
  | SelectCmd
  | ListCmd
  | LintCmd
  | LayoutCmd
  | UpdatePathNameCmd;

export interface ConditionEntry {
  propertyName: string;
  operator: string;
  value: string | number | boolean;
}

// ── Parse error ────────────────────────────────────────────────────────────

export interface DslParseError {
  line: number;
  message: string;
}

export interface ParseResult {
  commands: DslCommand[];
  errors: DslParseError[];
}

// ── Tokenizer helpers ──────────────────────────────────────────────────────

/** Split a line into tokens respecting double-quoted strings.
 *  Handles both standalone "quoted strings" and key:"quoted value" forms. */
function tokenize(line: string): string[] {
  const tokens: string[] = [];
  let i = 0;
  const len = line.length;

  while (i < len) {
    // skip whitespace
    while (i < len && (line[i] === " " || line[i] === "\t")) i++;
    if (i >= len) break;

    if (line[i] === '"') {
      // standalone quoted string — collect until closing quote
      i++; // skip opening "
      let str = "";
      while (i < len && line[i] !== '"') {
        if (line[i] === "\\" && i + 1 < len) {
          str += line[i + 1];
          i += 2;
        } else {
          str += line[i];
          i++;
        }
      }
      tokens.push(`"${str}"`);
      if (i < len) i++; // skip closing "
    } else {
      // unquoted token — but watch for key:"value with spaces" pattern
      let tok = "";
      while (i < len && line[i] !== " " && line[i] !== "\t") {
        if (line[i] === '"') {
          // mid-token quote (e.g. roleName:"HR Manager") — consume quoted part
          i++; // skip opening "
          tok += '"';
          while (i < len && line[i] !== '"') {
            if (line[i] === "\\" && i + 1 < len) {
              tok += line[i + 1];
              i += 2;
            } else {
              tok += line[i];
              i++;
            }
          }
          tok += '"';
          if (i < len) i++; // skip closing "
        } else {
          tok += line[i];
          i++;
        }
      }
      tokens.push(tok);
    }
  }

  return tokens;
}

/** Extract value from a key:value token. Returns undefined if key doesn't match. */
function extractKV(token: string, key: string): string | undefined {
  const prefix = key + ":";
  if (token.startsWith(prefix)) {
    return unquote(token.slice(prefix.length));
  }
  return undefined;
}

/** Remove surrounding quotes if present. */
function unquote(s: string): string {
  if (s.length >= 2 && s[0] === '"' && s[s.length - 1] === '"') {
    return s.slice(1, -1);
  }
  return s;
}

/**
 * Multilingual <number><unit> word aliases → canonical Kuika due unit.
 * Keeps the duration-from-label fallback locale-agnostic (the codebase
 * runs under many system locales — Turkish, English, German, …). Add a
 * language here rather than special-casing one. Keys are folded with a
 * plain `toLowerCase()` (locale-independent in JS) before lookup.
 */
const DURATION_TEXT_UNITS: Record<string, string> = {
  // Minutes
  minute: "Minutes",
  minutes: "Minutes",
  min: "Minutes",
  mins: "Minutes",
  dakika: "Minutes",
  dk: "Minutes",
  minuten: "Minutes",
  // Hours
  hour: "Hours",
  hours: "Hours",
  hr: "Hours",
  hrs: "Hours",
  saat: "Hours",
  stunden: "Hours",
  // Days
  day: "Days",
  days: "Days",
  gün: "Days",
  gun: "Days",
  tage: "Days",
  tag: "Days",
  // Weeks (runtime has no Week unit — normalized to Days ×7 downstream)
  week: "Weeks",
  weeks: "Weeks",
  hafta: "Weeks",
  woche: "Weeks",
  wochen: "Weeks",
  // Months
  month: "Months",
  months: "Months",
  ay: "Months",
  monat: "Months",
  monate: "Months",
  // Years
  year: "Years",
  years: "Years",
  yıl: "Years",
  yil: "Years",
  sene: "Years",
  jahr: "Years",
  jahre: "Years"
};

/**
 * Normalize a (value, canonical-unit) pair to a unit the Kuika runtime actually
 * supports. The timer/due model (`EWFDueUnit`) only has Minutes / Hours / Days,
 * so a coarser unit must be converted: "Weeks" → "Days" ×7 (5 weeks = 35 days).
 * Without this, "5 hafta"/"5Weeks" hit a hard parse error and the agent, unable
 * to satisfy it, started deleting existing elements to "fix" the script and
 * corrupted the diagram (KOA-17362). Months/Years are passed through unchanged
 * (their runtime handling is out of scope here). Exported for testing.
 */
export function normalizeDurationUnit(dueValue: number, canonicalUnit: string): { dueValue: number; dueUnit: string } {
  if (canonicalUnit === "Weeks") {
    return { dueValue: dueValue * 7, dueUnit: "Days" };
  }
  return { dueValue, dueUnit: canonicalUnit };
}

/**
 * Best-effort extraction of a wait duration from free-form label text like
 * "2 Saat Bekle", "Timer (2 saat)", "wait 2 hours", "30 dakika", "5 hafta".
 * Returns the FIRST `<number> <recognized-unit>` pair (normalized to a
 * runtime-supported unit), or undefined when none is found. Used as a fallback
 * for a TIMER whose label encodes the duration but whose author forgot the
 * explicit `due:` attribute (a common LLM slip). The explicit `due:` always
 * takes precedence over this heuristic.
 */
function parseDurationFromText(text: string | undefined): { dueValue: number; dueUnit: string } | undefined {
  if (!text) return undefined;
  const re = /(\d+)\s*([\p{L}]+)/gu;
  let match: RegExpExecArray | null;
  while ((match = re.exec(text)) !== null) {
    const unit = DURATION_TEXT_UNITS[match[2].toLowerCase()];
    if (unit) {
      return normalizeDurationUnit(parseInt(match[1], 10), unit);
    }
  }
  return undefined;
}

/** Parse a duration token like "5Hours", "30Minutes", "5Weeks" into { value,
 *  unit }, normalized to a runtime-supported unit (Weeks → Days ×7). */
function parseDuration(token: string): { dueValue: number; dueUnit: string } | undefined {
  const match = token.match(/^(\d+)(Minutes|Hours|Days|Weeks|Months|Years)$/i);
  if (!match) return undefined;

  const unitMap: Record<string, string> = {
    minutes: "Minutes",
    hours: "Hours",
    days: "Days",
    weeks: "Weeks",
    months: "Months",
    years: "Years"
  };

  const canonicalUnit: string = unitMap[match[2].toLowerCase()] || match[2];
  return normalizeDurationUnit(parseInt(match[1], 10), canonicalUnit);
}

/** Normalize operator aliases to canonical form. */
function normalizeOperator(op: string): string {
  const map: Record<string, string> = {
    "==": "isEqualTo",
    "!=": "isNotEqualTo",
    ">": "isGreaterThan",
    "<": "isLessThan",
    // KOA-18448 — the read side keys on the canonical names: an operator that is
    // not mapped reaches the generator as ">=", where the branch-expression switch
    // has no case for it and emits an EMPTY condition (and the multi-instance
    // quorum silently falls back to its default threshold).
    ">=": "isGreaterOrEqual",
    "<=": "isLessOrEqual",
    equals: "isEqualTo",
    isEqualTo: "isEqualTo",
    isNotEqualTo: "isNotEqualTo",
    isGreaterThan: "isGreaterThan",
    isGreaterOrEqual: "isGreaterOrEqual",
    isLessThan: "isLessThan",
    isLessOrEqual: "isLessOrEqual",
    isEmpty: "isEmpty",
    isNotEmpty: "isNotEmpty"
  };
  return map[op] || op;
}

/** Parse a value token into its typed form. */
function parseValue(token: string): string | number | boolean {
  if (token === "true") return true;
  if (token === "false") return false;
  const num = Number(token);
  if (!isNaN(num) && token.trim() !== "") return num;
  return unquote(token);
}

/** Find the index of a "->" token in the token list. */
function findArrow(tokens: string[]): number {
  return tokens.indexOf("->");
}

/**
 * Parse condition tokens starting from an index.
 * Format: WHEN <param> <op> <value> [OR <param> <op> <value>]...
 * Returns conditions array and whether it's an OR group.
 */
function parseConditions(
  tokens: string[],
  startIdx: number
): { conditions: ConditionEntry[]; isOrGroup: boolean; endIdx: number } | undefined {
  if (tokens[startIdx]?.toUpperCase() !== "WHEN") return undefined;

  const conditions: ConditionEntry[] = [];
  let isOrGroup = false;
  let i = startIdx + 1;

  while (i < tokens.length) {
    if (i + 1 >= tokens.length) break;

    const propertyName = tokens[i];
    const operator = normalizeOperator(tokens[i + 1]);

    // isEmpty/isNotEmpty have no value operand
    if (operator === "isEmpty" || operator === "isNotEmpty") {
      conditions.push({ propertyName, operator, value: "" });
      i += 2;
    } else {
      if (i + 2 >= tokens.length) break;
      const value = parseValue(tokens[i + 2]);
      conditions.push({ propertyName, operator, value });
      i += 3;
    }

    // Check for OR/AND separator
    if (i < tokens.length && tokens[i]?.toUpperCase() === "OR") {
      isOrGroup = true;
      i++;
    } else if (i < tokens.length && tokens[i]?.toUpperCase() === "AND") {
      i++;
    } else {
      break;
    }
  }

  if (conditions.length === 0) return undefined;
  return { conditions, isOrGroup, endIdx: i };
}

// ── Main parser ────────────────────────────────────────────────────────────

export function parseDsl(script: string): ParseResult {
  const commands: DslCommand[] = [];
  const errors: DslParseError[] = [];
  const lines = script.split("\n");

  for (let lineIdx = 0; lineIdx < lines.length; lineIdx++) {
    const rawLine = lines[lineIdx].trim();
    const lineNum = lineIdx + 1;

    // Skip blank lines and comments. `#` is the canonical marker, but LLMs
    // routinely emit `//` (and occasionally `--`) comment lines; tolerating
    // them prevents a single stray comment from cascading into "Unknown
    // command: //" + a wave of unresolved-alias failures.
    if (rawLine === "" || rawLine.startsWith("#") || rawLine.startsWith("//") || rawLine.startsWith("--")) continue;

    const tokens = tokenize(rawLine);
    if (tokens.length === 0) continue;

    const keyword = tokens[0].toUpperCase();

    try {
      switch (keyword) {
        case "PARAM":
          parseParamLine(tokens, lineNum, commands, errors);
          break;
        case "LANE":
          parseLaneLine(tokens, lineNum, commands, errors);
          break;
        case "TASK":
          parseTaskLine(tokens, lineNum, commands, errors);
          break;
        case "SYSTEM":
          parseSystemLine(tokens, lineNum, commands, errors);
          break;
        case "XGATEWAY":
          parseGatewayLine(tokens, lineNum, "Exclusive", "diverge", commands, errors);
          break;
        case "PGATEWAY":
          parseGatewayLine(tokens, lineNum, "Parallel", "diverge", commands, errors);
          break;
        case "IGATEWAY":
          parseGatewayLine(tokens, lineNum, "Inclusive", "diverge", commands, errors);
          break;
        case "PJOIN":
          parseGatewayLine(tokens, lineNum, "Parallel", "converge", commands, errors);
          break;
        case "IJOIN":
          parseGatewayLine(tokens, lineNum, "Inclusive", "converge", commands, errors);
          break;
        case "SUBPROCESS":
          parseSubprocessLine(tokens, lineNum, commands, errors);
          break;
        case "LINKED":
        // Aliases — LLMs guess these from the old per-op name
        // (add_linked_process) and the LINKED_CONFIG/RENAME/DELETE family.
        case "LINKED_PROCESS":
        case "CALL_ACTIVITY":
          parseLinkedLine(tokens, lineNum, commands, errors);
          break;
        case "TIMER":
          parseTimerLine(tokens, lineNum, commands, errors);
          break;
        case "BOUNDARY":
          parseBoundaryLine(tokens, lineNum, commands, errors);
          break;
        case "PATH":
          parsePathLine(tokens, lineNum, commands, errors);
          break;
        case "CONNECT":
          parseConnectLine(tokens, lineNum, commands, errors);
          break;
        case "CONDITION":
          parseConditionLine(tokens, lineNum, commands, errors);
          break;
        case "DEFAULT":
          parseDefaultLine(tokens, lineNum, commands, errors);
          break;
        case "RENAME":
          parseRenameLine(tokens, lineNum, commands, errors);
          break;
        case "DESCRIBE":
          parseDescribeLine(tokens, lineNum, commands, errors);
          break;
        case "DELETE":
          parseDeleteLine(tokens, lineNum, commands, errors);
          break;
        case "MI":
          parseMiLine(tokens, lineNum, commands, errors);
          break;
        case "MI_CANCEL":
          parseMiCancelLine(tokens, lineNum, commands, errors);
          break;
        case "MI_AGG":
          parseMiAggLine(tokens, lineNum, commands, errors);
          break;
        case "CONFIGURE_BOUNDARY":
          parseConfigureBoundaryLine(tokens, lineNum, commands, errors);
          break;
        case "TIMER_DUE":
          parseTimerDueLine(tokens, lineNum, commands, errors);
          break;
        case "TASK_DUE":
          parseTaskDueLine(tokens, lineNum, commands, errors);
          break;
        case "NOTIFY":
          parseNotifyLine(tokens, lineNum, commands, errors);
          break;
        case "INTERACTION":
          parseInteractionLine(tokens, lineNum, commands, errors);
          break;
        case "PRE_ACTION":
          parsePreActionLine(tokens, lineNum, commands, errors);
          break;
        case "STEP_ACTION":
          parseStepActionLine(tokens, lineNum, commands, errors);
          break;
        case "MAP_OUTPUT":
          parseMapOutputLine(tokens, lineNum, commands, errors);
          break;
        case "PHASE":
          parsePhaseLine(tokens, lineNum, commands, errors);
          break;
        case "PHASE_ASSIGN":
          parsePhaseAssignLine(tokens, lineNum, commands, errors);
          break;
        case "MAP_INPUT":
          parseMapInputLine(tokens, lineNum, commands, errors);
          break;
        case "ASSIGN_DYNAMIC":
          parseAssignDynamicLine(tokens, lineNum, commands, errors);
          break;
        case "ASSIGN_INPUT":
          parseAssignInputLine(tokens, lineNum, commands, errors);
          break;
        case "UPDATE_PARAM":
          parseUpdateParamLine(tokens, lineNum, commands, errors);
          break;
        case "DELETE_PARAM":
          parseDeleteParamLine(tokens, lineNum, commands, errors);
          break;
        case "LANE_DELETE":
          parseLaneDeleteLine(tokens, lineNum, commands, errors);
          break;
        case "LANE_RENAME":
          parseLaneRenameLine(tokens, lineNum, commands, errors);
          break;
        case "LANE_ASSIGN":
          parseLaneAssignLine(tokens, lineNum, commands, errors);
          break;
        case "LINKED_CONFIG":
          parseLinkedConfigLine(tokens, lineNum, commands, errors);
          break;
        case "LINKED_RENAME":
          parseLinkedRenameLine(tokens, lineNum, commands, errors);
          break;
        case "LINKED_DELETE":
          parseLinkedDeleteLine(tokens, lineNum, commands, errors);
          break;
        case "SUBPROCESS_RENAME":
          parseSubprocessRenameLine(tokens, lineNum, commands, errors);
          break;
        case "SUBPROCESS_DELETE":
          parseSubprocessDeleteLine(tokens, lineNum, commands, errors);
          break;
        case "ASSIGN_SCREEN":
          parseAssignScreenLine(tokens, lineNum, commands, errors);
          break;
        case "SCREEN_FIELD":
          parseScreenFieldLine(tokens, lineNum, commands, errors);
          break;
        case "SCREEN_SECTION":
          parseScreenSectionLine(tokens, lineNum, commands, errors);
          break;
        case "SCREEN_FORM":
          parseScreenFormLine(tokens, lineNum, commands, errors);
          break;
        case "SCREEN_BUTTONS":
          parseScreenButtonsLine(tokens, lineNum, commands, errors);
          break;
        // KOA-18431 — task-screen composition. See the command types for the two sigils.
        case "PLACE":
          parsePlaceLine(tokens, lineNum, commands, errors);
          break;
        case "CHILD":
          parseChildLine(tokens, lineNum, commands, errors);
          break;
        case "FIND":
          parseFindChildLine(tokens, lineNum, commands, errors);
          break;
        case "SET":
          parseSetPropertyLine(tokens, lineNum, commands, errors);
          break;
        case "BIND":
          parseBindPropertyLine(tokens, lineNum, commands, errors);
          break;
        case "DATA":
          parseSetDataActionLine(tokens, lineNum, commands, errors);
          break;
        case "DATAFIELD":
          parseSetDataFieldLine(tokens, lineNum, commands, errors);
          break;
        case "OPTION":
          parseSetOptionLine(tokens, lineNum, commands, errors);
          break;
        case "ACTION":
          parseAttachActionLine(tokens, lineNum, commands, errors);
          break;
        case "ARG":
          parseActionArgLine(tokens, lineNum, commands, errors);
          break;
        case "ORDER":
          parseReorderLine(tokens, lineNum, commands, errors);
          break;
        case "DROP":
          parseDropLine(tokens, lineNum, commands, errors);
          break;
        case "SELECT":
          parseSelectLine(tokens, lineNum, commands, errors);
          break;
        case "LIST": {
          // workflowId is an optional GUID. LLMs sometimes pass an alias like
          // `LIST @open` (treating it as a label) — an `@alias` is NEVER a
          // valid workflowId, so ignore it and LIST the currently-open
          // workflow instead of failing with a misleading "no workflow active".
          const listArg = tokens[1];
          const listWorkflowId = listArg && !listArg.startsWith("@") ? listArg : undefined;
          commands.push({ kind: "LIST", workflowId: listWorkflowId, line: lineNum });
          break;
        }
        case "LINT":
          commands.push({ kind: "LINT", line: lineNum });
          break;
        case "LAYOUT":
          commands.push({ kind: "LAYOUT", line: lineNum });
          break;
        case "UPDATE_PATH_NAME":
          parseUpdatePathNameLine(tokens, lineNum, commands, errors);
          break;
        default:
          errors.push({ line: lineNum, message: `Unknown command: ${tokens[0]}` });
      }
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      errors.push({ line: lineNum, message: msg });
    }
  }

  return { commands, errors };
}

// ── Line parsers ───────────────────────────────────────────────────────────

function parseParamLine(tokens: string[], lineNum: number, commands: DslCommand[], errors: DslParseError[]): void {
  // PARAM INTERNAL name Type [INPUT] [OUTPUT] [= default]
  // PARAM BO name Type [INPUT] [OUTPUT] [= default]
  const subtype = tokens[1]?.toUpperCase();
  if (subtype === "INTERNAL") {
    if (tokens.length < 4) {
      errors.push({ line: lineNum, message: "PARAM INTERNAL requires: name dataType [INPUT] [OUTPUT] [= default]" });
      return;
    }
    const name = tokens[2];
    const typeResult = normalizeWorkflowParamType(tokens[3]);
    if (!typeResult.ok) {
      errors.push({ line: lineNum, message: typeResult.message });
      return;
    }
    const upper = tokens.map((t) => t.toUpperCase());
    const isInput = upper.includes("INPUT");
    const isOutput = upper.includes("OUTPUT");
    let defaultValue: string | undefined;
    const eqIdx = tokens.indexOf("=");
    if (eqIdx !== -1 && eqIdx + 1 < tokens.length) {
      defaultValue = unquote(tokens[eqIdx + 1]);
    }
    commands.push({
      kind: "PARAM_INTERNAL",
      name,
      dataType: typeResult.type,
      isInput,
      isOutput,
      defaultValue,
      line: lineNum
    });
  } else if (subtype === "BO") {
    if (tokens.length < 4) {
      errors.push({
        line: lineNum,
        message: "PARAM BO requires: name dataType [INPUT] [OUTPUT] [= default], or name OBJECT <TypeName> [LIST]"
      });
      return;
    }
    const name = tokens[2];
    const upper = tokens.map((t) => t.toUpperCase());
    const isInput = upper.includes("INPUT");
    const isOutput = upper.includes("OUTPUT");

    // `OBJECT` is an explicit KEYWORD rather than "a type name we do not recognise":
    // a catalog entry may legitimately be called `String` or `Guid`, and guessing which
    // of the two vocabularies a bare word belongs to is exactly the ambiguity that would
    // silently persist the wrong shape. The keyword also keeps every existing scalar
    // line parsing byte-identically.
    if (upper[3] === "OBJECT") {
      const objectTypeName: string = unquote(tokens[4] ?? "");
      if (objectTypeName.length === 0) {
        errors.push({
          line: lineNum,
          message: "PARAM BO … OBJECT requires a Kuika Data Type name: PARAM BO <name> OBJECT <TypeName> [LIST]"
        });
        return;
      }
      // A POCO has no literal default. Accepting `= …` here would look like it worked and
      // then be dropped, so it is refused where the author can still see the line.
      if (tokens.includes("=")) {
        errors.push({
          line: lineNum,
          message:
            "An OBJECT parameter cannot take a default value — a data-type reference has no literal form. " +
            "Drop the `= …` part; the object arrives from a form field or a step output."
        });
        return;
      }
      commands.push({
        kind: "PARAM_BO",
        name,
        // The engine ignores `dataType` for objects, but the model requires a value —
        // the human parameter editor stores "String" here for the same reason.
        dataType: "String",
        isInput,
        isOutput,
        isObject: true,
        objectTypeName,
        isList: upper.includes("LIST"),
        line: lineNum
      });
      return;
    }

    const typeResult = normalizeWorkflowParamType(tokens[3]);
    if (!typeResult.ok) {
      errors.push({ line: lineNum, message: typeResult.message });
      return;
    }
    let defaultValue: string | undefined;
    const eqIdx = tokens.indexOf("=");
    if (eqIdx !== -1 && eqIdx + 1 < tokens.length) {
      defaultValue = unquote(tokens[eqIdx + 1]);
    }
    commands.push({
      kind: "PARAM_BO",
      name,
      dataType: typeResult.type,
      isInput,
      isOutput,
      defaultValue,
      line: lineNum
    });
  } else {
    errors.push({ line: lineNum, message: `Unknown PARAM type: ${tokens[1]}. Use INTERNAL or BO.` });
  }
}

function parseLaneLine(tokens: string[], lineNum: number, commands: DslCommand[], errors: DslParseError[]): void {
  // LANE @alias "name" [role:id roleName:"name" [assignmentType:N]]
  if (tokens.length < 3) {
    errors.push({ line: lineNum, message: 'LANE requires: @alias "name"' });
    return;
  }
  const alias = tokens[1];
  const name = unquote(tokens[2]);
  let roleId: string | undefined;
  let roleName: string | undefined;
  let assignmentType: number | undefined;

  for (let i = 3; i < tokens.length; i++) {
    const rv = extractKV(tokens[i], "role");
    if (rv !== undefined) {
      roleId = rv;
      continue;
    }
    const rn = extractKV(tokens[i], "roleName");
    if (rn !== undefined) {
      roleName = rn;
      continue;
    }
    const at = extractKV(tokens[i], "assignmentType");
    if (at !== undefined) {
      assignmentType = parseInt(at, 10);
      continue;
    }
  }

  commands.push({ kind: "LANE", alias, name, roleId, roleName, assignmentType, line: lineNum });
}

function parseNodeLine(
  tokens: string[],
  lineNum: number,
  nodeKind: "TASK" | "SYSTEM",
  commands: DslCommand[],
  errors: DslParseError[]
): void {
  // TASK/SYSTEM @alias "name" [after:ref] [before:ref] [lane:ref]
  if (tokens.length < 3) {
    errors.push({ line: lineNum, message: `${nodeKind} requires: @alias "name"` });
    return;
  }
  const alias = tokens[1];
  const name = unquote(tokens[2]);
  if (name.trim().length === 0) {
    errors.push({
      line: lineNum,
      message: `${nodeKind} requires a non-empty name (e.g. ${nodeKind} ${alias} "Step Name"). Do NOT create an unnamed placeholder step — if you only need to reach the process end, CONNECT the previous step directly to EndEvent_1 instead of inserting an empty step.`
    });
    return;
  }
  let afterNodeId: string | undefined;
  let beforeNodeId: string | undefined;
  let laneId: string | undefined;

  for (let i = 3; i < tokens.length; i++) {
    const a = extractKV(tokens[i], "after");
    if (a !== undefined) {
      afterNodeId = a;
      continue;
    }
    const b = extractKV(tokens[i], "before");
    if (b !== undefined) {
      beforeNodeId = b;
      continue;
    }
    const l = extractKV(tokens[i], "lane");
    if (l !== undefined) {
      laneId = l;
      continue;
    }
  }

  if (nodeKind === "TASK") {
    commands.push({ kind: "TASK", alias, name, afterNodeId, beforeNodeId, laneId, line: lineNum });
  } else {
    commands.push({ kind: "SYSTEM", alias, name, afterNodeId, beforeNodeId, laneId, line: lineNum });
  }
}

function parseTaskLine(t: string[], l: number, c: DslCommand[], e: DslParseError[]): void {
  parseNodeLine(t, l, "TASK", c, e);
}

function parseSystemLine(t: string[], l: number, c: DslCommand[], e: DslParseError[]): void {
  parseNodeLine(t, l, "SYSTEM", c, e);
}

function parseGatewayLine(
  tokens: string[],
  lineNum: number,
  gatewayType: "Exclusive" | "Inclusive" | "Parallel",
  role: "diverge" | "converge",
  commands: DslCommand[],
  errors: DslParseError[]
): void {
  if (tokens.length < 2) {
    errors.push({ line: lineNum, message: `Gateway requires: @alias` });
    return;
  }
  const alias = tokens[1];
  let name: string | undefined;
  let afterNodeId: string | undefined;
  let beforeNodeId: string | undefined;

  for (let i = 2; i < tokens.length; i++) {
    if (tokens[i].startsWith('"')) {
      name = unquote(tokens[i]);
      continue;
    }
    const a = extractKV(tokens[i], "after");
    if (a !== undefined) {
      afterNodeId = a;
      continue;
    }
    const b = extractKV(tokens[i], "before");
    if (b !== undefined) {
      beforeNodeId = b;
      continue;
    }
  }

  commands.push({ kind: "GATEWAY", alias, gatewayType, role, name, afterNodeId, beforeNodeId, line: lineNum });
}

function parseSubprocessLine(tokens: string[], lineNum: number, commands: DslCommand[], errors: DslParseError[]): void {
  if (tokens.length < 3) {
    errors.push({ line: lineNum, message: 'SUBPROCESS requires: @alias "name"' });
    return;
  }
  const alias = tokens[1];
  const name = unquote(tokens[2]);
  if (name.trim().length === 0) {
    errors.push({
      line: lineNum,
      message: `SUBPROCESS requires a non-empty name (e.g. SUBPROCESS ${alias} "Sub Process").`
    });
    return;
  }
  let afterNodeId: string | undefined;
  for (let i = 3; i < tokens.length; i++) {
    const a = extractKV(tokens[i], "after");
    if (a !== undefined) {
      afterNodeId = a;
      break;
    }
  }
  commands.push({ kind: "SUBPROCESS", alias, name, afterNodeId, line: lineNum });
}

function parseLinkedLine(tokens: string[], lineNum: number, commands: DslCommand[], errors: DslParseError[]): void {
  if (tokens.length < 2) {
    errors.push({ line: lineNum, message: "LINKED requires: @alias" });
    return;
  }
  const alias = tokens[1];
  let name: string | undefined;
  let afterNodeId: string | undefined;
  for (let i = 2; i < tokens.length; i++) {
    if (tokens[i].startsWith('"')) {
      name = unquote(tokens[i]);
      continue;
    }
    const a = extractKV(tokens[i], "after");
    if (a !== undefined) {
      afterNodeId = a;
      continue;
    }
  }
  commands.push({ kind: "LINKED", alias, name, afterNodeId, line: lineNum });
}

function parseTimerLine(tokens: string[], lineNum: number, commands: DslCommand[], errors: DslParseError[]): void {
  if (tokens.length < 3) {
    errors.push({ line: lineNum, message: "TIMER requires: @alias after:<ref>" });
    return;
  }
  const alias = tokens[1];
  let name: string | undefined;
  let afterNodeId: string | undefined;
  let beforeNodeId: string | undefined;
  let dueValue: number | undefined;
  let dueUnit: string | undefined;
  for (let i = 2; i < tokens.length; i++) {
    if (tokens[i].startsWith('"')) {
      name = unquote(tokens[i]);
      continue;
    }
    const a = extractKV(tokens[i], "after");
    if (a !== undefined) {
      afterNodeId = a;
      continue;
    }
    const b = extractKV(tokens[i], "before");
    if (b !== undefined) {
      beforeNodeId = b;
      continue;
    }
    const d = extractKV(tokens[i], "due");
    if (d !== undefined) {
      const parsed = parseDuration(d);
      if (parsed) {
        dueValue = parsed.dueValue;
        dueUnit = parsed.dueUnit;
      } else {
        errors.push({
          line: lineNum,
          message: `TIMER due: must be <N><Unit> (e.g. 2Hours, 30Minutes, 1Days, 5Weeks). Got "${d}".`
        });
        return;
      }
      continue;
    }
  }
  if (!afterNodeId) {
    errors.push({ line: lineNum, message: "TIMER requires after:<ref>" });
    return;
  }
  // Fallback: no explicit due: but the label encodes a duration
  // ("2 Saat Bekle", "Timer (2 saat)", "wait 2 hours"). Without this the
  // timer silently defaults to 1 day. Explicit due: always wins.
  if (dueValue === undefined) {
    const fromLabel = parseDurationFromText(name);
    if (fromLabel) {
      dueValue = fromLabel.dueValue;
      dueUnit = fromLabel.dueUnit;
    }
  }
  commands.push({ kind: "TIMER", alias, name, afterNodeId, beforeNodeId, dueValue, dueUnit, line: lineNum });
}

function parseBoundaryLine(tokens: string[], lineNum: number, commands: DslCommand[], errors: DslParseError[]): void {
  if (tokens.length < 4) {
    errors.push({ line: lineNum, message: "BOUNDARY requires: @alias on:<ref> type:<Timer|Message|Error>" });
    return;
  }
  const alias = tokens[1];
  let nodeId: string | undefined;
  let eventType: string | undefined;
  for (let i = 2; i < tokens.length; i++) {
    const on = extractKV(tokens[i], "on");
    if (on !== undefined) {
      nodeId = on;
      continue;
    }
    const tp = extractKV(tokens[i], "type");
    if (tp !== undefined) {
      eventType = tp;
      continue;
    }
  }
  if (!nodeId || !eventType) {
    errors.push({ line: lineNum, message: "BOUNDARY requires on:<ref> and type:<Timer|Message|Error>" });
    return;
  }

  const typeMap: Record<string, string> = {
    timer: "TimerEventDefinition",
    message: "MessageEventDefinition",
    error: "ErrorEventDefinition",
    timereventdefinition: "TimerEventDefinition",
    messageeventdefinition: "MessageEventDefinition",
    erroreventdefinition: "ErrorEventDefinition"
  };
  const resolvedType = typeMap[eventType.toLowerCase()];
  if (!resolvedType) {
    errors.push({ line: lineNum, message: `Unknown boundary event type: ${eventType}` });
    return;
  }

  commands.push({
    kind: "BOUNDARY",
    alias,
    nodeId,
    eventType: resolvedType as BoundaryCmd["eventType"],
    line: lineNum
  });
}

function parsePathLine(tokens: string[], lineNum: number, commands: DslCommand[], errors: DslParseError[]): void {
  // PATH <source> -> <target> ["name"] [DEFAULT] [WHEN param op value]
  const arrowIdx = findArrow(tokens);
  if (arrowIdx === -1 || arrowIdx < 2 || arrowIdx + 1 >= tokens.length) {
    errors.push({ line: lineNum, message: "PATH requires: <source> -> <target>" });
    return;
  }
  const sourceNodeId = tokens[arrowIdx - 1];
  const targetNodeId = tokens[arrowIdx + 1];
  let pathName: string | undefined;
  let isDefault = false;
  let conditions: ConditionEntry[] | undefined;
  let isOrGroup: boolean | undefined;

  let i = arrowIdx + 2;
  while (i < tokens.length) {
    if (tokens[i].startsWith('"')) {
      pathName = unquote(tokens[i]);
      i++;
    } else if (tokens[i].toUpperCase() === "DEFAULT") {
      isDefault = true;
      i++;
    } else if (tokens[i].toUpperCase() === "WHEN") {
      const result = parseConditions(tokens, i);
      if (result) {
        conditions = result.conditions;
        isOrGroup = result.isOrGroup;
        i = result.endIdx;
      } else {
        i++;
      }
    } else {
      i++;
    }
  }

  commands.push({
    kind: "PATH",
    sourceNodeId,
    targetNodeId,
    pathName,
    isDefault,
    conditions,
    isOrGroup,
    line: lineNum
  });
}

function parseConnectLine(tokens: string[], lineNum: number, commands: DslCommand[], errors: DslParseError[]): void {
  const arrowIdx = findArrow(tokens);
  if (arrowIdx === -1 || arrowIdx < 2 || arrowIdx + 1 >= tokens.length) {
    errors.push({ line: lineNum, message: "CONNECT requires: <source> -> <target>" });
    return;
  }
  commands.push({
    kind: "CONNECT",
    sourceNodeId: tokens[arrowIdx - 1],
    targetNodeId: tokens[arrowIdx + 1],
    line: lineNum
  });
}

function parseConditionLine(tokens: string[], lineNum: number, commands: DslCommand[], errors: DslParseError[]): void {
  // CONDITION <pathRef> WHEN param op value [OR ...]
  if (tokens.length < 5) {
    errors.push({ line: lineNum, message: "CONDITION requires: <pathRef> WHEN <param> <op> <value>" });
    return;
  }
  const pathId = tokens[1];
  const result = parseConditions(tokens, 2);
  if (!result || result.conditions.length === 0) {
    errors.push({ line: lineNum, message: "CONDITION: could not parse conditions" });
    return;
  }
  commands.push({
    kind: "CONDITION",
    pathId,
    conditions: result.conditions,
    isOrGroup: result.isOrGroup,
    line: lineNum
  });
}

function parseDefaultLine(tokens: string[], lineNum: number, commands: DslCommand[], errors: DslParseError[]): void {
  // Tolerate the PATH-style arrow form `DEFAULT <gw> -> <ref>` — LLMs
  // routinely carry the `->` over from PATH syntax. Drop a standalone `->`
  // token so both `DEFAULT gw flowOrTarget` and `DEFAULT gw -> flowOrTarget`
  // parse identically. The second ref may be a SequenceFlow id OR a target
  // node id; the action resolves a target node to the gateway→target flow.
  const cleaned = tokens.filter((t) => t !== "->");
  if (cleaned.length < 3) {
    errors.push({ line: lineNum, message: "DEFAULT requires: <gatewayRef> <pathRef>" });
    return;
  }
  // Optional 4th token is a quoted label for the default branch:
  // `DEFAULT <gw> <pathOrTarget> "Diğer Durumlar"`.
  const pathName: string | undefined = cleaned[3] !== undefined ? unquote(cleaned[3]) : undefined;
  commands.push({ kind: "DEFAULT", gatewayId: cleaned[1], pathId: cleaned[2], pathName, line: lineNum });
}

function parseRenameLine(tokens: string[], lineNum: number, commands: DslCommand[], errors: DslParseError[]): void {
  if (tokens.length < 3) {
    errors.push({ line: lineNum, message: 'RENAME requires: <ref> "newName"' });
    return;
  }
  commands.push({ kind: "RENAME", elementId: tokens[1], newName: unquote(tokens[2]), line: lineNum });
}

function parseDescribeLine(tokens: string[], lineNum: number, commands: DslCommand[], errors: DslParseError[]): void {
  if (tokens.length < 3) {
    errors.push({ line: lineNum, message: 'DESCRIBE requires: <ref> "description text"' });
    return;
  }
  commands.push({ kind: "DESCRIBE", stepId: tokens[1], description: unquote(tokens[2]), line: lineNum });
}

function parseDeleteLine(tokens: string[], lineNum: number, commands: DslCommand[], errors: DslParseError[]): void {
  if (tokens.length < 2) {
    errors.push({ line: lineNum, message: "DELETE requires: <ref>" });
    return;
  }
  commands.push({ kind: "DELETE", elementId: tokens[1], line: lineNum });
}

function parseMiLine(tokens: string[], lineNum: number, commands: DslCommand[], errors: DslParseError[]): void {
  // MI <ref> <PMI|SMI|None> [count:N] [collection:Manual|Assignment] [expansion:ByRole|ByUser] [ordering:Asc|Desc]
  if (tokens.length < 3) {
    errors.push({ line: lineNum, message: "MI requires: <ref> <PMI|SMI|None>" });
    return;
  }
  const stepId = tokens[1];
  const mode = tokens[2] as MiCmd["mode"];
  let instanceCount: number | undefined;
  let collectionSource: "Manual" | "Assignment" | undefined;
  let expansionMode: "ByRole" | "ByUser" | undefined;
  let ordering: "Ascending" | "Descending" | undefined;

  for (let i = 3; i < tokens.length; i++) {
    const c = extractKV(tokens[i], "count");
    if (c !== undefined) {
      instanceCount = parseInt(c, 10);
      continue;
    }
    const cs = extractKV(tokens[i], "collection");
    if (cs !== undefined) {
      collectionSource = cs as MiCmd["collectionSource"];
      continue;
    }
    const em = extractKV(tokens[i], "expansion");
    if (em !== undefined) {
      expansionMode = em as MiCmd["expansionMode"];
      continue;
    }
    const o = extractKV(tokens[i], "ordering");
    if (o !== undefined) {
      ordering = o as MiCmd["ordering"];
      continue;
    }
  }

  commands.push({ kind: "MI", stepId, mode, instanceCount, collectionSource, expansionMode, ordering, line: lineNum });
}

function parseMiCancelLine(tokens: string[], lineNum: number, commands: DslCommand[], errors: DslParseError[]): void {
  if (tokens.length < 5) {
    errors.push({ line: lineNum, message: "MI_CANCEL requires: <ref> WHEN <param> <op> <value>" });
    return;
  }
  const stepId = tokens[1];
  const result = parseConditions(tokens, 2);
  if (!result) {
    errors.push({ line: lineNum, message: "MI_CANCEL: could not parse conditions" });
    return;
  }
  commands.push({
    kind: "MI_CANCEL",
    stepId,
    conditions: result.conditions,
    isOrGroup: result.isOrGroup,
    line: lineNum
  });
}

function parseMiAggLine(tokens: string[], lineNum: number, commands: DslCommand[], errors: DslParseError[]): void {
  if (tokens.length < 4) {
    errors.push({ line: lineNum, message: "MI_AGG requires: <ref> childKey:<key> strategy:<strategy>" });
    return;
  }
  const stepId = tokens[1];
  let childOutputKey: string | undefined;
  let strategy: string | undefined;
  for (let i = 2; i < tokens.length; i++) {
    const ck = extractKV(tokens[i], "childKey");
    if (ck !== undefined) {
      childOutputKey = ck;
      continue;
    }
    const s = extractKV(tokens[i], "strategy");
    if (s !== undefined) {
      strategy = s;
      continue;
    }
  }
  if (!childOutputKey || !strategy) {
    errors.push({ line: lineNum, message: "MI_AGG requires childKey: and strategy:" });
    return;
  }
  commands.push({ kind: "MI_AGG", stepId, childOutputKey, strategy, line: lineNum });
}

function parseConfigureBoundaryLine(
  tokens: string[],
  lineNum: number,
  commands: DslCommand[],
  errors: DslParseError[]
): void {
  if (tokens.length < 3) {
    errors.push({
      line: lineNum,
      message: "CONFIGURE_BOUNDARY requires: <ref> [interrupting|noninterrupting] [duration:NUnit]"
    });
    return;
  }
  const eventId = tokens[1];
  let isInterrupting = true;
  let dueValue: number | undefined;
  let dueUnit: string | undefined;

  for (let i = 2; i < tokens.length; i++) {
    if (tokens[i].toLowerCase() === "interrupting") {
      isInterrupting = true;
      continue;
    }
    if (tokens[i].toLowerCase() === "noninterrupting") {
      isInterrupting = false;
      continue;
    }
    const d = extractKV(tokens[i], "duration");
    if (d !== undefined) {
      const parsed = parseDuration(d);
      if (parsed) {
        dueValue = parsed.dueValue;
        dueUnit = parsed.dueUnit;
      }
      continue;
    }
  }

  commands.push({ kind: "CONFIGURE_BOUNDARY", eventId, isInterrupting, dueValue, dueUnit, line: lineNum });
}

function parseTimerDueLine(tokens: string[], lineNum: number, commands: DslCommand[], errors: DslParseError[]): void {
  if (tokens.length < 3) {
    errors.push({ line: lineNum, message: "TIMER_DUE requires: <ref> <N><Unit>" });
    return;
  }
  const eventId = tokens[1];
  const dur = parseDuration(tokens[2]);
  if (!dur) {
    errors.push({ line: lineNum, message: `Invalid duration: ${tokens[2]}. Use format like 5Hours, 30Minutes.` });
    return;
  }
  commands.push({ kind: "TIMER_DUE", eventId, ...dur, line: lineNum });
}

function parseTaskDueLine(tokens: string[], lineNum: number, commands: DslCommand[], errors: DslParseError[]): void {
  if (tokens.length < 3) {
    errors.push({ line: lineNum, message: "TASK_DUE requires: <ref> <N><Unit>" });
    return;
  }
  const taskId = tokens[1];
  const dur = parseDuration(tokens[2]);
  if (!dur) {
    errors.push({ line: lineNum, message: `Invalid duration: ${tokens[2]}` });
    return;
  }
  commands.push({ kind: "TASK_DUE", taskId, ...dur, line: lineNum });
}

function parseNotifyLine(tokens: string[], lineNum: number, commands: DslCommand[], errors: DslParseError[]): void {
  if (tokens.length < 3) {
    errors.push({ line: lineNum, message: "NOTIFY requires: <ref> <on|off>" });
    return;
  }
  const taskId = tokens[1];
  const sendNotification = tokens[2].toLowerCase() === "on";
  commands.push({ kind: "NOTIFY", taskId, sendNotification, line: lineNum });
}

function parseInteractionLine(
  tokens: string[],
  lineNum: number,
  commands: DslCommand[],
  errors: DslParseError[]
): void {
  if (tokens.length < 3) {
    errors.push({ line: lineNum, message: 'INTERACTION requires: <ref> "label"' });
    return;
  }
  // Optional decision payload after the label: `SET <name>=<value> [<name>=<value>]...`.
  // The tokenizer keeps each `name=value` as ONE token (a quoted value like
  // status="Approved" is folded in whole), so we split on the FIRST `=`. A bare
  // `SET` keyword (case-insensitive) is tolerated and may repeat; any non-`SET`
  // token without an `=` is ignored (lenient, matching the other line parsers).
  const parameters: InteractionSetAssignment[] = [];
  for (let i = 3; i < tokens.length; i++) {
    if (tokens[i].toUpperCase() === "SET") continue;
    const eqIdx = tokens[i].indexOf("=");
    if (eqIdx > 0) {
      const name = tokens[i].slice(0, eqIdx);
      const value = unquote(tokens[i].slice(eqIdx + 1));
      parameters.push({ name, value });
    }
  }
  commands.push({ kind: "INTERACTION", taskId: tokens[1], label: unquote(tokens[2]), parameters, line: lineNum });
}

function parsePreActionLine(tokens: string[], lineNum: number, commands: DslCommand[], errors: DslParseError[]): void {
  if (tokens.length < 3) {
    errors.push({ line: lineNum, message: 'PRE_ACTION requires: <ref> "actionName"' });
    return;
  }
  commands.push({ kind: "PRE_ACTION", taskId: tokens[1], actionName: unquote(tokens[2]), line: lineNum });
}

function parseStepActionLine(tokens: string[], lineNum: number, commands: DslCommand[], errors: DslParseError[]): void {
  if (tokens.length < 3) {
    errors.push({ line: lineNum, message: 'STEP_ACTION requires: <ref> "actionName"' });
    return;
  }
  commands.push({ kind: "STEP_ACTION", stepId: tokens[1], actionName: unquote(tokens[2]), line: lineNum });
}

// MAP_OUTPUT <ref> "<parameterNameOrId>" [FROM <actionName>[.<resultField>]]
//
// The optional FROM clause names the VALUE SOURCE of a system step's output: the
// step action whose result the parameter takes, plus the field to extract. A
// mapped output with no source stays a fixed-value symbol with no value, which
// codegen emits as `= ""` — the parameter is silently always empty downstream
// (KOA-17362: `ReturnRequestId` / `ReturnRecordId` were mapped but never sourced,
// so every step keyed on them read an empty id). The clause is optional because
// a USER task's outputs are written by its INTERACTION buttons or task form, and
// because a system step with an unambiguous match is auto-bound
// (`resolveAnchoredOutputSource`); the `kuika/step-output-unbound` lint catches
// whatever is still unsourced.
function parseMapOutputLine(tokens: string[], lineNum: number, commands: DslCommand[], errors: DslParseError[]): void {
  if (tokens.length < 3) {
    errors.push({ line: lineNum, message: 'MAP_OUTPUT requires: <ref> "parameterNameOrId"' });
    return;
  }
  const cmd: MapOutputCmd = {
    kind: "MAP_OUTPUT",
    nodeId: tokens[1],
    parameterId: unquote(tokens[2]),
    line: lineNum
  };
  const fromIdx: number = tokens.findIndex((t: string): boolean => t.toUpperCase() === "FROM");
  if (fromIdx >= 3) {
    const rawSource: string = unquote(tokens.slice(fromIdx + 1).join(" ")).trim();
    if (rawSource.length === 0) {
      errors.push({
        line: lineNum,
        message: "MAP_OUTPUT FROM requires a source: FROM <actionName>[.<resultField>]"
      });
      return;
    }
    // Split on the LAST dot so an action name containing dots still resolves and
    // only a trailing single-segment field is peeled off.
    const dotIdx: number = rawSource.lastIndexOf(".");
    if (dotIdx > 0 && dotIdx < rawSource.length - 1) {
      cmd.sourceActionName = rawSource.slice(0, dotIdx);
      cmd.sourceFieldName = rawSource.slice(dotIdx + 1);
    } else {
      cmd.sourceActionName = rawSource;
    }
  }
  commands.push(cmd);
}

// PHASE "<phaseName>"
// Declares a Happy Path phase. The name may contain spaces, so everything after
// the keyword is taken as the name (quotes optional but conventional).
function parsePhaseLine(tokens: string[], lineNum: number, commands: DslCommand[], errors: DslParseError[]): void {
  const name: string = unquote(tokens.slice(1).join(" "));
  if (name.length === 0) {
    errors.push({ line: lineNum, message: 'PHASE requires: PHASE "<phaseName>"' });
    return;
  }
  commands.push({ kind: "PHASE", name, line: lineNum });
}

// PHASE_ASSIGN <stepRef> "<phaseName>"
// Attaches a step to a declared phase. Step-ref first, mirroring every other
// step-scoped command (STEP_ACTION / MAP_INPUT / MAP_OUTPUT / ASSIGN_SCREEN /
// SCREEN_FIELD). The phase name may contain spaces.
function parsePhaseAssignLine(
  tokens: string[],
  lineNum: number,
  commands: DslCommand[],
  errors: DslParseError[]
): void {
  const stepId: string = tokens[1] ?? "";
  const phaseName: string = unquote(tokens.slice(2).join(" "));
  if (stepId.length === 0 || phaseName.length === 0) {
    errors.push({ line: lineNum, message: 'PHASE_ASSIGN requires: PHASE_ASSIGN <ref> "<phaseName>"' });
    return;
  }
  commands.push({ kind: "PHASE_ASSIGN", stepId, phaseName, line: lineNum });
}

// MAP_INPUT <stepRef> <actionParamName> [=] <workflowParamName | "literal">
// Binds an attached step-action's INPUT parameter to a workflow parameter
// (by name) or a fixed literal value. The `=` separator is optional.
function parseMapInputLine(tokens: string[], lineNum: number, commands: DslCommand[], errors: DslParseError[]): void {
  const stepId: string = tokens[1];
  const parameterName: string = unquote(tokens[2] ?? "");
  const eqIdx: number = tokens.indexOf("=");
  const rawSource: string = eqIdx >= 0 ? tokens.slice(eqIdx + 1).join(" ") : (tokens[3] ?? "");
  const source: string = unquote(rawSource);
  // A quoted source ("…") is an explicit fixed LITERAL; a bare token is a
  // REFERENCE to a workflow parameter by name. Preserve that intent so the
  // executor never silently coerces a bare (unresolved) param name into literal
  // text — the tokenizer keeps surrounding quotes on quoted tokens.
  const isLiteral: boolean = rawSource.length >= 2 && rawSource[0] === '"' && rawSource[rawSource.length - 1] === '"';
  if (!stepId || !parameterName || !source) {
    errors.push({
      line: lineNum,
      message: 'MAP_INPUT requires: <stepRef> <actionParamName> = <workflowParamName | "literal">'
    });
    return;
  }
  commands.push({ kind: "MAP_INPUT", stepId, parameterName, source, isLiteral, line: lineNum });
}

// ASSIGN_DYNAMIC <taskRef> "<actionName>" returns:<User|Role>
// KOA-18593 — the user task is assigned by an action that returns who gets it. `returns` is
// required: a person's user id and a role's name are different things to the engine.
function parseAssignDynamicLine(
  tokens: string[],
  lineNum: number,
  commands: DslCommand[],
  errors: DslParseError[]
): void {
  const usage: string = 'ASSIGN_DYNAMIC requires: <taskRef> "<actionName>" returns:<User|Role>';
  const taskId: string = tokens[1] ?? "";
  const actionName: string = unquote(tokens[2] ?? "");
  let rawReturns: string = "";
  for (const token of tokens.slice(3)) {
    const value: string | undefined = extractKV(token, "returns");
    if (value === undefined) continue;
    rawReturns = value.trim().toLowerCase();
    break;
  }
  if (taskId.length === 0 || actionName.length === 0 || (rawReturns !== "user" && rawReturns !== "role")) {
    errors.push({ line: lineNum, message: usage });
    return;
  }
  const returns: "User" | "Role" = rawReturns === "role" ? "Role" : "User";
  commands.push({ kind: "ASSIGN_DYNAMIC", taskId, actionName, returns, line: lineNum });
}

// ASSIGN_INPUT <taskRef> <actionParamName> [=] <workflowParamName | "literal">
// KOA-18593 — binds an input of the task's dynamic assignment action, with MAP_INPUT's grammar.
function parseAssignInputLine(
  tokens: string[],
  lineNum: number,
  commands: DslCommand[],
  errors: DslParseError[]
): void {
  const taskId: string = tokens[1] ?? "";
  const parameterName: string = unquote(tokens[2] ?? "");
  const eqIdx: number = tokens.indexOf("=");
  const rawSource: string = eqIdx >= 0 ? tokens.slice(eqIdx + 1).join(" ") : (tokens[3] ?? "");
  const source: string = unquote(rawSource);
  const isLiteral: boolean = rawSource.length >= 2 && rawSource[0] === '"' && rawSource[rawSource.length - 1] === '"';
  if (taskId.length === 0 || parameterName.length === 0 || source.length === 0) {
    errors.push({
      line: lineNum,
      message: 'ASSIGN_INPUT requires: <taskRef> <actionParamName> = <workflowParamName | "literal">'
    });
    return;
  }
  commands.push({ kind: "ASSIGN_INPUT", taskId, parameterName, source, isLiteral, line: lineNum });
}

function parseUpdateParamLine(
  tokens: string[],
  lineNum: number,
  commands: DslCommand[],
  errors: DslParseError[]
): void {
  if (tokens.length < 3) {
    errors.push({ line: lineNum, message: "UPDATE_PARAM requires: <paramId> and at least one key:value update" });
    return;
  }
  const parameterId = tokens[1];
  const updates: UpdateParamCmd["updates"] = {};

  for (let i = 2; i < tokens.length; i++) {
    const n = extractKV(tokens[i], "name");
    if (n !== undefined) {
      updates.name = n;
      continue;
    }
    const t = extractKV(tokens[i], "type");
    if (t !== undefined) {
      const typeResult = normalizeWorkflowParamType(t);
      if (!typeResult.ok) {
        errors.push({ line: lineNum, message: typeResult.message });
        return;
      }
      updates.dataType = typeResult.type;
      continue;
    }
    const d = extractKV(tokens[i], "default");
    if (d !== undefined) {
      updates.defaultValue = d;
      continue;
    }
    const inp = extractKV(tokens[i], "input");
    if (inp !== undefined) {
      updates.isInput = inp === "true";
      continue;
    }
    const out = extractKV(tokens[i], "output");
    if (out !== undefined) {
      updates.isOutput = out === "true";
      continue;
    }
  }

  commands.push({ kind: "UPDATE_PARAM", parameterId, updates, line: lineNum });
}

function parseDeleteParamLine(
  tokens: string[],
  lineNum: number,
  commands: DslCommand[],
  errors: DslParseError[]
): void {
  if (tokens.length < 2) {
    errors.push({ line: lineNum, message: "DELETE_PARAM requires: <paramId>" });
    return;
  }
  commands.push({ kind: "DELETE_PARAM", parameterId: tokens[1], line: lineNum });
}

function parseLaneDeleteLine(tokens: string[], lineNum: number, commands: DslCommand[], errors: DslParseError[]): void {
  if (tokens.length < 2) {
    errors.push({ line: lineNum, message: "LANE_DELETE requires: <ref>" });
    return;
  }
  commands.push({ kind: "LANE_DELETE", laneId: tokens[1], line: lineNum });
}

function parseLaneRenameLine(tokens: string[], lineNum: number, commands: DslCommand[], errors: DslParseError[]): void {
  if (tokens.length < 3) {
    errors.push({ line: lineNum, message: 'LANE_RENAME requires: <ref> "newName"' });
    return;
  }
  commands.push({ kind: "LANE_RENAME", laneId: tokens[1], newName: unquote(tokens[2]), line: lineNum });
}

function parseLaneAssignLine(tokens: string[], lineNum: number, commands: DslCommand[], errors: DslParseError[]): void {
  // KOA-18136 — the ROLE NAME is the contract; the id is bound by the platform from the project's
  // own roles before the script is dispatched. A line that names its role and writes no id is
  // correct authoring, so the shape check asks for a reference plus one role clause, not for an id.
  if (tokens.length < 3) {
    errors.push({ line: lineNum, message: 'LANE_ASSIGN requires: <ref> roleName:"<name>"' });
    return;
  }
  const laneId = tokens[1];
  let roleId: string | undefined;
  let roleName = "Assigned Role";
  let assignmentType = 2;

  for (let i = 2; i < tokens.length; i++) {
    const r = extractKV(tokens[i], "role");
    if (r !== undefined) {
      roleId = r;
      continue;
    }
    const rn = extractKV(tokens[i], "roleName");
    if (rn !== undefined) {
      roleName = rn;
      continue;
    }
    const at = extractKV(tokens[i], "assignmentType");
    if (at !== undefined) {
      assignmentType = parseInt(at, 10);
      continue;
    }
  }

  if (!roleId && roleName === "Assigned Role") {
    errors.push({ line: lineNum, message: 'LANE_ASSIGN requires roleName:"<role name>"' });
    return;
  }

  commands.push({ kind: "LANE_ASSIGN", laneId, roleId, roleName, assignmentType, line: lineNum });
}

function parseLinkedConfigLine(
  tokens: string[],
  lineNum: number,
  commands: DslCommand[],
  errors: DslParseError[]
): void {
  // LINKED_CONFIG <ref> target:<processId> [map:"src"->"tgt"]...
  if (tokens.length < 3) {
    errors.push({ line: lineNum, message: "LINKED_CONFIG requires: <ref> target:<processId>" });
    return;
  }
  const linkedProcessId = tokens[1];
  let targetProcessId: string | undefined;
  const parameters: { sourceParameterName: string; targetParameterName: string }[] = [];

  for (let i = 2; i < tokens.length; i++) {
    const t = extractKV(tokens[i], "target");
    if (t !== undefined) {
      targetProcessId = t;
      continue;
    }
    const m = extractKV(tokens[i], "map");
    if (m !== undefined) {
      // format: "srcParam->tgtParam"
      const parts = m.split("->");
      if (parts.length === 2) {
        parameters.push({
          sourceParameterName: parts[0].trim(),
          targetParameterName: parts[1].trim()
        });
      }
      continue;
    }
  }

  if (!targetProcessId) {
    errors.push({ line: lineNum, message: "LINKED_CONFIG requires target:<processId>" });
    return;
  }

  commands.push({ kind: "LINKED_CONFIG", linkedProcessId, targetProcessId, parameters, line: lineNum });
}

function parseLinkedRenameLine(
  tokens: string[],
  lineNum: number,
  commands: DslCommand[],
  errors: DslParseError[]
): void {
  if (tokens.length < 3) {
    errors.push({ line: lineNum, message: 'LINKED_RENAME requires: <ref> "newName"' });
    return;
  }
  commands.push({ kind: "LINKED_RENAME", linkedProcessId: tokens[1], newName: unquote(tokens[2]), line: lineNum });
}

function parseLinkedDeleteLine(
  tokens: string[],
  lineNum: number,
  commands: DslCommand[],
  errors: DslParseError[]
): void {
  if (tokens.length < 2) {
    errors.push({ line: lineNum, message: "LINKED_DELETE requires: <ref>" });
    return;
  }
  commands.push({ kind: "LINKED_DELETE", linkedProcessId: tokens[1], line: lineNum });
}

function parseSubprocessRenameLine(
  tokens: string[],
  lineNum: number,
  commands: DslCommand[],
  errors: DslParseError[]
): void {
  if (tokens.length < 3) {
    errors.push({ line: lineNum, message: 'SUBPROCESS_RENAME requires: <ref> "newName"' });
    return;
  }
  commands.push({ kind: "SUBPROCESS_RENAME", subprocessId: tokens[1], newName: unquote(tokens[2]), line: lineNum });
}

function parseSubprocessDeleteLine(
  tokens: string[],
  lineNum: number,
  commands: DslCommand[],
  errors: DslParseError[]
): void {
  if (tokens.length < 2) {
    errors.push({ line: lineNum, message: "SUBPROCESS_DELETE requires: <ref>" });
    return;
  }
  commands.push({ kind: "SUBPROCESS_DELETE", subprocessId: tokens[1], line: lineNum });
}

function parseAssignScreenLine(
  tokens: string[],
  lineNum: number,
  commands: DslCommand[],
  errors: DslParseError[]
): void {
  // ASSIGN_SCREEN <processId> <stepRef> [<stepRef>...]
  if (tokens.length < 3) {
    errors.push({ line: lineNum, message: "ASSIGN_SCREEN requires: <processId> <stepRef> [<stepRef>...]" });
    return;
  }
  const processId = tokens[1];
  const stepIds = tokens.slice(2);
  commands.push({ kind: "ASSIGN_SCREEN", processId, stepIds, line: lineNum });
}

const SCREEN_FIELD_USAGE: string =
  'SCREEN_FIELD requires: <stepRef> "<boParam>" [hidden|show] [readonly|editable] [required|optional] [label:"<text>"] [order:<n>] [element:<Widget>] [scale:<n>] [section:"<title>"]';

/** Column counts a form section supports (`ECRUDFormLayoutType`: 1, 2 or 4). */
const SUPPORTED_SECTION_COLUMNS: readonly number[] = [1, 2, 4];

interface SectionLayoutTokens {
  columns?: number;
  tablet?: number;
  mobile?: number;
  /** Set when a token was neither a layout option nor parseable — caller stops. */
  error?: string;
}

/**
 * Parse the shared `[columns:N] [tablet:N] [mobile:N]` tail of SCREEN_SECTION /
 * SCREEN_FORM. Fails closed on an unsupported option or an unsupported column count
 * (only 1, 2 and 4 exist) so a silently-ignored layout can never look applied.
 */
function parseSectionLayoutTokens(tokens: string[], startIndex: number): SectionLayoutTokens {
  const layout: SectionLayoutTokens = {};
  for (let i = startIndex; i < tokens.length; i++) {
    const token: string = tokens[i];
    const key: "columns" | "tablet" | "mobile" | undefined = (["columns", "tablet", "mobile"] as const).find(
      (candidate: "columns" | "tablet" | "mobile"): boolean => extractKV(token, candidate) !== undefined
    );
    if (key === undefined) {
      return {
        ...layout,
        error: `unsupported option '${token}'. Supported: columns:<1|2|4>, tablet:<1|2|4>, mobile:<1|2|4>.`
      };
    }
    const raw: string = extractKV(token, key) ?? "";
    const parsed: number = parseInt(raw, 10);
    if (!SUPPORTED_SECTION_COLUMNS.includes(parsed)) {
      return { ...layout, error: `a form section can be laid out in 1, 2 or 4 columns (got '${raw}' for ${key}).` };
    }
    layout[key] = parsed;
  }
  return layout;
}

function parseScreenSectionLine(
  tokens: string[],
  lineNum: number,
  commands: DslCommand[],
  errors: DslParseError[]
): void {
  // SCREEN_SECTION <stepRef> "<title>" [columns:N] [tablet:N] [mobile:N]
  if (tokens.length < 3) {
    errors.push({
      line: lineNum,
      message: 'SCREEN_SECTION requires: <stepRef> "<title>" [columns:<1|2|4>] [tablet:<1|2|4>] [mobile:<1|2|4>]'
    });
    return;
  }
  const stepId: string = tokens[1];
  const title: string = unquote(tokens[2]);
  if (title.trim().length === 0) {
    errors.push({ line: lineNum, message: 'SCREEN_SECTION requires a non-empty "<title>".' });
    return;
  }
  const layout: SectionLayoutTokens = parseSectionLayoutTokens(tokens, 3);
  if (layout.error) {
    errors.push({ line: lineNum, message: `SCREEN_SECTION: ${layout.error}` });
    return;
  }
  commands.push({
    kind: "SCREEN_SECTION",
    stepId,
    title,
    columns: layout.columns,
    tablet: layout.tablet,
    mobile: layout.mobile,
    line: lineNum
  });
}

const SCREEN_BUTTONS_LOCATIONS: readonly string[] = ["under", "underform", "form", "top", "header", "bottom", "footer"];

function parseScreenButtonsLine(
  tokens: string[],
  lineNum: number,
  commands: DslCommand[],
  errors: DslParseError[]
): void {
  // SCREEN_BUTTONS <under|top|bottom>   (project-wide — deliberately no step ref)
  if (tokens.length < 2) {
    errors.push({ line: lineNum, message: "SCREEN_BUTTONS requires: <under|top|bottom>" });
    return;
  }
  const location: string = unquote(tokens[1]);
  if (!SCREEN_BUTTONS_LOCATIONS.includes(location.trim().toLowerCase())) {
    errors.push({
      line: lineNum,
      message: `SCREEN_BUTTONS location '${location}' is not valid. Use under, top or bottom.`
    });
    return;
  }
  commands.push({ kind: "SCREEN_BUTTONS", location, line: lineNum });
}

function parseScreenFormLine(tokens: string[], lineNum: number, commands: DslCommand[], errors: DslParseError[]): void {
  // SCREEN_FORM <stepRef> [columns:N] [tablet:N] [mobile:N]
  if (tokens.length < 2) {
    errors.push({
      line: lineNum,
      message: "SCREEN_FORM requires: <stepRef> [columns:<1|2|4>] [tablet:<1|2|4>] [mobile:<1|2|4>]"
    });
    return;
  }
  const layout: SectionLayoutTokens = parseSectionLayoutTokens(tokens, 2);
  if (layout.error) {
    errors.push({ line: lineNum, message: `SCREEN_FORM: ${layout.error}` });
    return;
  }
  commands.push({
    kind: "SCREEN_FORM",
    stepId: tokens[1],
    columns: layout.columns,
    tablet: layout.tablet,
    mobile: layout.mobile,
    line: lineNum
  });
}

function parseScreenFieldLine(
  tokens: string[],
  lineNum: number,
  commands: DslCommand[],
  errors: DslParseError[]
): void {
  // SCREEN_FIELD <stepRef> "<boParam>" [hidden|show] [readonly|editable] [required|optional] [label:"…"] [order:N]
  if (tokens.length < 3) {
    errors.push({ line: lineNum, message: SCREEN_FIELD_USAGE });
    return;
  }
  const stepId: string = tokens[1];
  const param: string = unquote(tokens[2]);
  if (param.trim().length === 0) {
    errors.push({ line: lineNum, message: SCREEN_FIELD_USAGE });
    return;
  }

  let hidden: boolean | undefined;
  let isReadonly: boolean | undefined;
  let isRequired: boolean | undefined;
  let label: string | undefined;
  let order: number | undefined;
  let element: string | undefined;
  let scale: number | undefined;
  let section: string | undefined;

  for (let i = 3; i < tokens.length; i++) {
    const token: string = tokens[i];
    const flag: string = token.toLowerCase();
    if (flag === "hidden") {
      hidden = true;
      continue;
    }
    if (flag === "show" || flag === "visible") {
      hidden = false;
      continue;
    }
    if (flag === "readonly") {
      isReadonly = true;
      continue;
    }
    if (flag === "editable") {
      isReadonly = false;
      continue;
    }
    if (flag === "required") {
      isRequired = true;
      continue;
    }
    if (flag === "optional") {
      isRequired = false;
      continue;
    }
    const labelValue: string | undefined = extractKV(token, "label");
    if (labelValue !== undefined) {
      label = unquote(labelValue);
      continue;
    }
    const orderValue: string | undefined = extractKV(token, "order");
    if (orderValue !== undefined) {
      const parsed: number = parseInt(orderValue, 10);
      if (Number.isNaN(parsed)) {
        errors.push({
          line: lineNum,
          message: `SCREEN_FIELD order must be a number (got '${orderValue}'). Example: \`order:2\`.`
        });
        return;
      }
      order = parsed;
      continue;
    }
    const elementValue: string | undefined = extractKV(token, "element");
    if (elementValue !== undefined) {
      element = unquote(elementValue);
      continue;
    }
    const sectionValue: string | undefined = extractKV(token, "section");
    if (sectionValue !== undefined) {
      section = unquote(sectionValue);
      continue;
    }
    const scaleValue: string | undefined = extractKV(token, "scale");
    if (scaleValue !== undefined) {
      const parsedScale: number = parseInt(scaleValue, 10);
      if (Number.isNaN(parsedScale)) {
        errors.push({
          line: lineNum,
          message: `SCREEN_FIELD scale must be a number (got '${scaleValue}'). Example: \`scale:2\`.`
        });
        return;
      }
      scale = parsedScale;
      continue;
    }
    // Fail closed — an option we silently ignored would look applied but do
    // nothing, and the author would never learn the right spelling.
    errors.push({
      line: lineNum,
      message: `SCREEN_FIELD: unsupported option '${token}'. Supported: hidden|show, readonly|editable, required|optional, label:"<text>", order:<n>, element:<Widget>, scale:<n>, section:"<title>".`
    });
    return;
  }

  commands.push({
    kind: "SCREEN_FIELD",
    stepId,
    param,
    hidden,
    isReadonly,
    isRequired,
    label,
    order,
    element,
    scale,
    section,
    line: lineNum
  });
}

function parseSelectLine(tokens: string[], lineNum: number, commands: DslCommand[], errors: DslParseError[]): void {
  if (tokens.length < 2) {
    errors.push({ line: lineNum, message: "SELECT requires: <ref>" });
    return;
  }
  commands.push({ kind: "SELECT", stepId: tokens[1], line: lineNum });
}

function parseUpdatePathNameLine(
  tokens: string[],
  lineNum: number,
  commands: DslCommand[],
  errors: DslParseError[]
): void {
  if (tokens.length < 3) {
    errors.push({ line: lineNum, message: 'UPDATE_PATH_NAME requires: <pathRef> "newName"' });
    return;
  }
  commands.push({ kind: "UPDATE_PATH_NAME", pathId: tokens[1], newName: unquote(tokens[2]), line: lineNum });
}

// ── Task-screen composition (KOA-18431) ────────────────────────────────────

/**
 * The screen's own roots, under the words an author uses for them.
 *
 * TYPE-ONLY dependency on the model's enum, the same choice `screen-composition-payload` makes and
 * for the same reason: importing it as a RUNTIME value ties this parser to a build artifact, and a
 * stale transpiled sibling would make the ids arrive `undefined` while the types still check out.
 * `satisfies` still fails the build if one of these stops being a real root.
 */
const SCREEN_ROOT_ANCHORS = {
  screen: -1,
  header: -2,
  footer: -3,
  pageheader: -4,
  pagefooter: -5,
  layoutheader: -10,
  layoutfooter: -11,
  layoutleft: -12,
  layoutright: -13
} as const satisfies Record<string, SpecialDIIds>;

/** Fold an anchor word so `pageHeader`, `page-header` and `page_header` are one name. */
function foldAnchorWord(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[\s\-_]/g, "");
}

const ANCHOR_WORDS: string = Object.keys(SCREEN_ROOT_ANCHORS).join(", ");

/** A screen-side alias — a component or an action instance this script named. */
function isScreenAlias(token: string | undefined): boolean {
  return typeof token === "string" && token.startsWith("#") && token.length > 1;
}

/**
 * Where a placement goes, or a removal takes from.
 *
 * A bare number is accepted because a script that edits an EXISTING screen has no alias for what is
 * already on it — the ids come from the same place every other ref does, the tool's own read.
 */
function parseCompositionAnchor(token: string | undefined): CompositionAnchor | undefined {
  if (token === undefined) return undefined;
  if (isScreenAlias(token)) return { kind: "alias", alias: token };

  const rootId: number | undefined = (SCREEN_ROOT_ANCHORS as Record<string, number>)[foldAnchorWord(token)];
  if (rootId !== undefined) return { kind: "root", kuikaId: rootId };

  const numeric: number = Number.parseInt(token, 10);
  if (Number.isFinite(numeric) && String(numeric) === token.trim()) return { kind: "kuikaId", kuikaId: numeric };

  return undefined;
}

const SOURCE_FORMS: string =
  '"<literal>", process:<name>, screenInput:<name>, component:#alias.<property>, action:#alias, ' +
  "field:#alias.<Field>, row:<Field>, default:<Name>";

interface ISourceParseFailure {
  error: string;
}

function isSourceFailure(result: CompositionSource | ISourceParseFailure): result is ISourceParseFailure {
  return (result as ISourceParseFailure).error !== undefined;
}

/**
 * One token → one binding source.
 *
 * Every form states its own operands, and a form that is missing one is REFUSED here rather than
 * carried forward: a source with a blank name resolves to nothing, and a binding that resolves to
 * nothing is a property that silently ships empty.
 */
function parseCompositionSource(token: string | undefined): CompositionSource | ISourceParseFailure {
  if (token === undefined || token.trim().length === 0) {
    return { error: `a source is required. One of: ${SOURCE_FORMS}.` };
  }

  // A quoted token is a literal, exactly as it is everywhere else in this grammar.
  if (token.startsWith('"') && token.endsWith('"') && token.length >= 2) {
    return { kind: "fixed", value: unquote(token) };
  }

  const separator: number = token.indexOf(":");
  if (separator <= 0) {
    return {
      error:
        `"${token}" is not a source. Quote it to mean a literal value, or name where the value comes ` +
        `from: ${SOURCE_FORMS}.`
    };
  }

  const prefix: string = token.slice(0, separator).toLowerCase();
  const rest: string = unquote(token.slice(separator + 1)).trim();
  if (rest.length === 0) {
    return { error: `"${token}" names no value — ${prefix}: needs what comes after the colon.` };
  }

  if (prefix === "process") return { kind: "processParameter", name: rest };
  if (prefix === "screeninput") return { kind: "screenInput", name: rest };
  if (prefix === "row") return { kind: "currentRow", fieldName: rest };
  if (prefix === "default") return { kind: "default", name: rest };

  if (prefix === "action") {
    if (!isScreenAlias(rest)) return { error: `action: takes the #alias of an action this script attached.` };
    return { kind: "actionResult", actionAlias: rest };
  }

  if (prefix === "component" || prefix === "field") {
    const dot: number = rest.indexOf(".");
    if (dot <= 0 || dot === rest.length - 1) {
      return {
        error:
          prefix === "component"
            ? `component: takes #alias.<property> — which component, and which of its values.`
            : `field: takes #alias.<Field> — which action, and which field of its result.`
      };
    }
    const alias: string = rest.slice(0, dot);
    const member: string = rest.slice(dot + 1);
    if (!isScreenAlias(alias)) {
      return { error: `${prefix}: takes a #alias before the dot.` };
    }
    return prefix === "component"
      ? { kind: "componentValue", alias, propertyName: member }
      : { kind: "actionField", actionAlias: alias, fieldName: member };
  }

  return { error: `Unknown source "${prefix}:". Use one of: ${SOURCE_FORMS}.` };
}

const PLACE_USAGE: string = `PLACE requires: <stepRef> <ComponentName> AS #alias IN <${ANCHOR_WORDS}|#alias|kuikaId>`;

function parsePlaceLine(tokens: string[], lineNum: number, commands: DslCommand[], errors: DslParseError[]): void {
  // PLACE <stepRef> <ComponentName> AS #alias IN <anchor>
  const asIndex: number = tokens.findIndex((token: string): boolean => token.toUpperCase() === "AS");
  const inIndex: number = tokens.findIndex((token: string): boolean => token.toUpperCase() === "IN");
  if (tokens.length < 7 || asIndex !== 3 || inIndex !== 5) {
    errors.push({ line: lineNum, message: PLACE_USAGE });
    return;
  }

  const stepId: string = tokens[1];
  const componentName: string = unquote(tokens[2]);
  const alias: string = tokens[4];
  if (!isScreenAlias(alias)) {
    errors.push({ line: lineNum, message: `PLACE names the placed component with a #alias (got "${alias}").` });
    return;
  }
  if (componentName.length === 0) {
    errors.push({ line: lineNum, message: PLACE_USAGE });
    return;
  }

  const anchor: CompositionAnchor | undefined = parseCompositionAnchor(tokens[6]);
  if (anchor === undefined) {
    errors.push({
      line: lineNum,
      message:
        `PLACE … IN "${tokens[6]}" — that is not a place on the screen. Use a screen root ` +
        `(${ANCHOR_WORDS}), a #alias this script placed, or a component id.`
    });
    return;
  }

  commands.push({ kind: "PLACE", stepId, componentName, alias, anchor, line: lineNum });
}

const CHILD_USAGE: string = "CHILD requires: #host <ComponentName> AS #alias [first]";

function parseChildLine(tokens: string[], lineNum: number, commands: DslCommand[], errors: DslParseError[]): void {
  // CHILD #host <ComponentName> AS #alias [first]
  const asIndex: number = tokens.findIndex((token: string): boolean => token.toUpperCase() === "AS");
  if (tokens.length < 5 || asIndex !== 3) {
    errors.push({ line: lineNum, message: CHILD_USAGE });
    return;
  }
  const hostAlias: string = tokens[1];
  const alias: string = tokens[4];
  if (!isScreenAlias(hostAlias) || !isScreenAlias(alias)) {
    errors.push({ line: lineNum, message: CHILD_USAGE });
    return;
  }
  const componentName: string = unquote(tokens[2]);
  if (componentName.length === 0) {
    errors.push({ line: lineNum, message: CHILD_USAGE });
    return;
  }
  const placeFirst: boolean = tokens.slice(5).some((token: string): boolean => token.toLowerCase() === "first");

  commands.push({ kind: "CHILD", hostAlias, componentName, alias, placeFirst, line: lineNum });
}

const FIND_USAGE: string =
  "FIND requires: <stepRef|#host> <ComponentName> AS #alias [n] — names something ALREADY on the " +
  "screen. A placed Table arrives with its TableRow and that row's first TableDefaultColumn already " +
  "built; a step ref searches that step's screen body, which is how a script reaches what an " +
  "earlier script left there";

function parseFindChildLine(tokens: string[], lineNum: number, commands: DslCommand[], errors: DslParseError[]): void {
  // FIND <stepRef|#host> <ComponentName> AS #alias [n]
  const asIndex: number = tokens.findIndex((token: string): boolean => token.toUpperCase() === "AS");
  if (tokens.length < 5 || asIndex !== 3) {
    errors.push({ line: lineNum, message: FIND_USAGE });
    return;
  }
  const host: CompositionHost = isScreenAlias(tokens[1])
    ? { kind: "alias", alias: tokens[1] }
    : { kind: "step", stepRef: tokens[1] };
  const alias: string = tokens[4];
  if (tokens[1].length === 0 || !isScreenAlias(alias)) {
    errors.push({ line: lineNum, message: FIND_USAGE });
    return;
  }
  const componentName: string = unquote(tokens[2]);
  if (componentName.length === 0) {
    errors.push({ line: lineNum, message: FIND_USAGE });
    return;
  }

  let ordinal: number = 1;
  if (tokens.length > 5) {
    const parsed: number = Number.parseInt(tokens[5], 10);
    if (!Number.isFinite(parsed) || parsed < 1) {
      errors.push({
        line: lineNum,
        message: `FIND … AS ${alias} "${tokens[5]}" — the last token picks WHICH same-named child, counting from 1.`
      });
      return;
    }
    ordinal = parsed;
  }

  commands.push({ kind: "FIND", host, componentName, alias, ordinal, line: lineNum });
}

const SET_USAGE: string = 'SET requires: #alias <propertyName> [=] <value>   (e.g. SET #saveBtn label = "Kaydet")';

function parseSetPropertyLine(
  tokens: string[],
  lineNum: number,
  commands: DslCommand[],
  errors: DslParseError[]
): void {
  // SET #alias <property> [=] <value>
  if (tokens.length < 4 || !isScreenAlias(tokens[1])) {
    errors.push({ line: lineNum, message: SET_USAGE });
    return;
  }
  const propertyName: string = unquote(tokens[2]);
  // The `=` is optional — it reads naturally and the model writes it either way.
  const valueIndex: number = tokens[3] === "=" ? 4 : 3;
  const rawValue: string | undefined = tokens[valueIndex];
  if (propertyName.length === 0 || rawValue === undefined) {
    errors.push({ line: lineNum, message: SET_USAGE });
    return;
  }

  commands.push({
    kind: "SET",
    alias: tokens[1],
    propertyName,
    value: parseValue(rawValue),
    line: lineNum
  });
}

const BIND_USAGE: string = `BIND requires: #alias <propertyName> <source>   Sources: ${SOURCE_FORMS}`;

function parseBindPropertyLine(
  tokens: string[],
  lineNum: number,
  commands: DslCommand[],
  errors: DslParseError[]
): void {
  // BIND #alias <property> <source>
  if (tokens.length < 4 || !isScreenAlias(tokens[1])) {
    errors.push({ line: lineNum, message: BIND_USAGE });
    return;
  }
  const propertyName: string = unquote(tokens[2]);
  if (propertyName.length === 0) {
    errors.push({ line: lineNum, message: BIND_USAGE });
    return;
  }
  const source: CompositionSource | ISourceParseFailure = parseCompositionSource(tokens[3]);
  if (isSourceFailure(source)) {
    errors.push({ line: lineNum, message: `BIND ${tokens[1]} ${propertyName}: ${source.error}` });
    return;
  }

  commands.push({ kind: "BIND", alias: tokens[1], propertyName, source, line: lineNum });
}

const DATA_USAGE: string =
  'DATA requires: #alias <propertyName> "<ActionName>"   (or `fixed` for a literal option list)';

function parseSetDataActionLine(
  tokens: string[],
  lineNum: number,
  commands: DslCommand[],
  errors: DslParseError[]
): void {
  // DATA #alias <property> "<ActionName>" | fixed
  if (tokens.length < 4 || !isScreenAlias(tokens[1])) {
    errors.push({ line: lineNum, message: DATA_USAGE });
    return;
  }
  const propertyName: string = unquote(tokens[2]);
  const actionName: string = unquote(tokens[3]);
  if (propertyName.length === 0 || actionName.length === 0) {
    errors.push({ line: lineNum, message: DATA_USAGE });
    return;
  }

  commands.push({ kind: "DATA", alias: tokens[1], propertyName, actionName, line: lineNum });
}

const DATAFIELD_USAGE: string =
  "DATAFIELD requires: #alias <propertyName> <key> <value>   (e.g. DATAFIELD #kategori options fieldToDisplay Name)";

function parseSetDataFieldLine(
  tokens: string[],
  lineNum: number,
  commands: DslCommand[],
  errors: DslParseError[]
): void {
  // DATAFIELD #alias <property> <key> <value>
  if (tokens.length < 5 || !isScreenAlias(tokens[1])) {
    errors.push({ line: lineNum, message: DATAFIELD_USAGE });
    return;
  }
  const propertyName: string = unquote(tokens[2]);
  const key: string = unquote(tokens[3]);
  const value: string = unquote(tokens[4]);
  if (propertyName.length === 0 || key.length === 0) {
    errors.push({ line: lineNum, message: DATAFIELD_USAGE });
    return;
  }

  commands.push({ kind: "DATAFIELD", alias: tokens[1], propertyName, key, value, line: lineNum });
}

const OPTION_USAGE: string =
  'OPTION requires: #alias <propertyName> "<value>" ["<label>"]   (one choice per line, e.g. ' +
  'OPTION #kategori options "Seyahat" "Seyahat Gideri"). The label defaults to the value.';

function parseSetOptionLine(tokens: string[], lineNum: number, commands: DslCommand[], errors: DslParseError[]): void {
  // OPTION #alias <property> "<value>" ["<label>"]
  if (tokens.length < 4 || !isScreenAlias(tokens[1])) {
    errors.push({ line: lineNum, message: OPTION_USAGE });
    return;
  }
  const propertyName: string = unquote(tokens[2]);
  const value: string = unquote(tokens[3]);
  if (propertyName.length === 0 || value.length === 0) {
    errors.push({ line: lineNum, message: OPTION_USAGE });
    return;
  }
  // A single operand means the stored value and the visible label are the same word, which is the
  // common case for a status or a category and reads better than writing it twice.
  const label: string = tokens.length > 4 ? unquote(tokens[4]) : value;

  commands.push({ kind: "OPTION", alias: tokens[1], propertyName, value, label, line: lineNum });
}

const ACTION_USAGE: string =
  'ACTION requires: <stepRef|#alias> <eventName> "<ActionName>" [AS #actionAlias] — the step ref ' +
  "puts the action on the SCREEN BODY, which is where a screen-load read (pageInit) belongs; a " +
  "#alias puts it on that component, for its own events (a Button's onClick)";

function parseAttachActionLine(
  tokens: string[],
  lineNum: number,
  commands: DslCommand[],
  errors: DslParseError[]
): void {
  // ACTION <stepRef|#alias> <event> "<ActionName>" [AS #actionAlias]
  if (tokens.length < 4 || tokens[1] === undefined || tokens[1].length === 0) {
    errors.push({ line: lineNum, message: ACTION_USAGE });
    return;
  }
  const host: CompositionHost = isScreenAlias(tokens[1])
    ? { kind: "alias", alias: tokens[1] }
    : { kind: "step", stepRef: tokens[1] };
  const eventName: string = unquote(tokens[2]);
  const actionName: string = unquote(tokens[3]);
  if (eventName.length === 0 || actionName.length === 0) {
    errors.push({ line: lineNum, message: ACTION_USAGE });
    return;
  }

  let actionAlias: string | undefined;
  const asIndex: number = tokens.findIndex((token: string): boolean => token.toUpperCase() === "AS");
  if (asIndex >= 4) {
    const candidate: string | undefined = tokens[asIndex + 1];
    if (!isScreenAlias(candidate)) {
      errors.push({ line: lineNum, message: `ACTION … AS takes a #alias for the attached action.` });
      return;
    }
    actionAlias = candidate;
  }

  commands.push({
    kind: "ACTION",
    host,
    eventName,
    actionName,
    ...(actionAlias !== undefined ? { actionAlias } : {}),
    line: lineNum
  });
}

const ARG_USAGE: string = `ARG requires: #actionAlias "<parameterName>" <source>   Sources: ${SOURCE_FORMS}`;

function parseActionArgLine(tokens: string[], lineNum: number, commands: DslCommand[], errors: DslParseError[]): void {
  // ARG #actionAlias "<parameter>" <source>
  if (tokens.length < 4 || !isScreenAlias(tokens[1])) {
    errors.push({ line: lineNum, message: ARG_USAGE });
    return;
  }
  const parameterName: string = unquote(tokens[2]);
  if (parameterName.length === 0) {
    errors.push({ line: lineNum, message: ARG_USAGE });
    return;
  }
  const source: CompositionSource | ISourceParseFailure = parseCompositionSource(tokens[3]);
  if (isSourceFailure(source)) {
    errors.push({ line: lineNum, message: `ARG ${tokens[1]} ${parameterName}: ${source.error}` });
    return;
  }

  commands.push({ kind: "ARG", actionAlias: tokens[1], parameterName, source, line: lineNum });
}

const ORDER_USAGE: string = "ORDER requires: #alias BEFORE #sibling";

function parseReorderLine(tokens: string[], lineNum: number, commands: DslCommand[], errors: DslParseError[]): void {
  // ORDER #alias BEFORE #sibling
  if (tokens.length < 4 || tokens[2].toUpperCase() !== "BEFORE") {
    errors.push({ line: lineNum, message: ORDER_USAGE });
    return;
  }
  if (!isScreenAlias(tokens[1]) || !isScreenAlias(tokens[3])) {
    errors.push({ line: lineNum, message: ORDER_USAGE });
    return;
  }

  commands.push({ kind: "ORDER", alias: tokens[1], beforeAlias: tokens[3], line: lineNum });
}

const DROP_USAGE: string =
  "DROP requires: #alias — the name of a component this script PLACEd or FOUND, or of an action it " +
  "attached. To remove something already on the screen, name it first with " +
  "`FIND <stepRef> <ComponentName> AS #alias`";

function parseDropLine(tokens: string[], lineNum: number, commands: DslCommand[], errors: DslParseError[]): void {
  // DROP #alias | DROP <kuikaId>
  const target: CompositionAnchor | undefined = parseCompositionAnchor(tokens[1]);
  if (target === undefined) {
    errors.push({ line: lineNum, message: DROP_USAGE });
    return;
  }
  if (target.kind === "root") {
    errors.push({
      line: lineNum,
      message: `DROP ${tokens[1]} — a screen root is part of the screen itself and cannot be removed.`
    });
    return;
  }

  commands.push({ kind: "DROP", target, line: lineNum });
}

/**
 * The screen BODY's own events — the ones that fire without anybody touching the screen.
 *
 * A read attached here runs while the screen is coming up. Nothing the assignee has done has
 * happened yet, so every control on that screen still holds its empty starting value, and an input
 * of that read fed from one of them is fed with nothing. Live `c74f551e`: a query whose SQL
 * required a category was attached to `pageInit` and given the category from the SelectBox beside
 * it — the query ran once, with an empty category, and the table showed "No data found" forever.
 *
 * Kept next to the grammar because two layers ask the same question — the validator before the
 * round is spent, the applier at the line — and a second copy would let them disagree. It mirrors
 * `ROOT_SECTIONS["-1"].availableEvents`, which is the platform's own statement of what the body
 * declares; a test pins the two together.
 */
export const SCREEN_LOAD_EVENTS: ReadonlySet<string> = new Set<string>(["pageinit", "recurring"]);

/** Whether an event name is one the screen body raises by itself. */
export function isScreenLoadEvent(eventName: string): boolean {
  return SCREEN_LOAD_EVENTS.has(eventName.trim().toLowerCase());
}

/**
 * The lines that COMPOSE a task screen rather than draw the diagram.
 *
 * Declared here, with the command interfaces themselves, because three parties need the same
 * answer and were each carrying their own copy: the executor (to hold these lines out of the main
 * loop), the validator (to check them before a round is spent) and the composition pass (to apply
 * them). Same list, same alias rules, one spelling.
 */
export type ScreenCompositionCommand =
  | PlaceCmd
  | ChildCmd
  | FindChildCmd
  | SetPropertyCmd
  | BindPropertyCmd
  | SetDataActionCmd
  | SetDataFieldCmd
  | SetOptionCmd
  | AttachActionCmd
  | ActionArgCmd
  | ReorderCmd
  | DropCmd;

const SCREEN_COMPOSITION_KINDS: ReadonlySet<string> = new Set<string>([
  "PLACE",
  "CHILD",
  "FIND",
  "SET",
  "BIND",
  "DATA",
  "DATAFIELD",
  "OPTION",
  "ACTION",
  "ARG",
  "ORDER",
  "DROP"
]);

/** Whether a command is composed onto a task screen rather than dispatched in the main loop. */
export function isScreenCompositionCommand(command: DslCommand): command is ScreenCompositionCommand {
  return SCREEN_COMPOSITION_KINDS.has(command.kind);
}

/** The alias a line brings into being, if it brings one. */
export function compositionDeclaredAlias(command: ScreenCompositionCommand): string | undefined {
  if (command.kind === "PLACE" || command.kind === "CHILD" || command.kind === "FIND") return command.alias;
  if (command.kind === "ACTION") return command.actionAlias;
  return undefined;
}

/** Every alias a line READS, with the word for what it was being used as. */
export function compositionReferencedAliases(command: ScreenCompositionCommand): [string, string][] {
  const referenced: [string, string][] = [];
  const fromSource = (source: CompositionSource): void => {
    if (source.kind === "componentValue") referenced.push([source.alias, "The component named in"]);
    if (source.kind === "actionResult" || source.kind === "actionField") {
      referenced.push([source.actionAlias, "The action named in"]);
    }
  };

  if (command.kind === "PLACE" && command.anchor.kind === "alias") {
    referenced.push([command.anchor.alias, "The container"]);
  }
  if (command.kind === "CHILD") referenced.push([command.hostAlias, "The host"]);
  if (command.kind === "FIND" && command.host.kind === "alias") referenced.push([command.host.alias, "The host"]);
  if (command.kind === "SET" || command.kind === "DATA" || command.kind === "DATAFIELD" || command.kind === "OPTION") {
    referenced.push([command.alias, "The component"]);
  }
  if (command.kind === "BIND") {
    referenced.push([command.alias, "The component"]);
    fromSource(command.source);
  }
  if (command.kind === "ACTION" && command.host.kind === "alias") {
    referenced.push([command.host.alias, "The component"]);
  }
  if (command.kind === "ARG") {
    referenced.push([command.actionAlias, "The action"]);
    fromSource(command.source);
  }
  if (command.kind === "ORDER") {
    referenced.push([command.alias, "The component"]);
    referenced.push([command.beforeAlias, "The sibling"]);
  }
  if (command.kind === "DROP" && command.target.kind === "alias") {
    referenced.push([command.target.alias, "The component"]);
  }
  return referenced;
}

/**
 * KOA-18431 — the script's lines, ordered so a line never runs before the line that DECLARES the
 * alias it reads.
 *
 * ## The defect this closes
 *
 * The grammar says, and has always said, *"Line order does not matter"*, and two of the three
 * layers that resolve an alias agreed: the validator counts an alias declared ANYWHERE in the
 * script (`composition-undefined-alias` never compares line numbers), and the step router builds
 * alias→step over the whole script before anything runs, saying so in its own docstring. The
 * executor did not: it walked the lines in order and refused anything that read an alias a later
 * line would create. Live `f5b1be9e` is the cost — the builder wrote the natural shape
 *
 *     PLACE … Table AS #previous        ACTION #previous … AS #query
 *     ARG #query "ExpenseCategoryId" component:#categoryFilter.value      ← line 4
 *     PLACE … SelectBox AS #categoryFilter                                 ← line 6
 *
 * and both screens landed their table, action and datasource while the ONE line that makes the
 * filter filter was refused. Sixteen of eighteen lines succeeded, so nothing looked broken.
 *
 * ## Why deferral rather than a sort
 *
 * Order is not free at the canvas: components append, and `ORDER … BEFORE` speaks about siblings.
 * A topological sort would be free to hoist a later `PLACE` above an earlier one and quietly
 * reshuffle the screen. So a line only ever moves LATER, never earlier: each sweep emits, in
 * script order, every line whose references are already declared, and defers the rest. Two lines
 * therefore keep their relative order unless the earlier one had to wait for the later one — the
 * minimum change that makes the published contract true.
 *
 * Lines that can never be satisfied (an alias nothing declares, or a cycle) are emitted last, in
 * script order, and refuse themselves with their own message.
 */
export function orderByDeclaration(commands: readonly ScreenCompositionCommand[]): ScreenCompositionCommand[] {
  const declared: Set<string> = new Set<string>();
  const pending: ScreenCompositionCommand[] = [...commands];
  const ordered: ScreenCompositionCommand[] = [];

  let progressed: boolean = true;
  while (pending.length > 0 && progressed) {
    progressed = false;
    for (let index = 0; index < pending.length;) {
      const command: ScreenCompositionCommand = pending[index];
      const waitingOn: boolean = compositionReferencedAliases(command).some(
        ([alias]: [string, string]): boolean => !declared.has(alias)
      );
      if (waitingOn) {
        index += 1;
        continue;
      }
      ordered.push(command);
      const declares: string | undefined = compositionDeclaredAlias(command);
      if (declares !== undefined) declared.add(declares);
      pending.splice(index, 1);
      progressed = true;
    }
  }
  ordered.push(...pending);
  return ordered;
}
